/**
 * Every user-facing string in the Castfeeds web app, as [English, Hebrew].
 * Keys and wording follow the prototype (design/Castfeeds App.dc.html, STRS); new keys use the same voice.
 * Placeholders use {name} and are filled by t(); keep the same placeholders in both languages.
 */
export const strings = {
  // ── App & common ─────────────────────────────────────────────
  appName: ['Castfeeds', 'Castfeeds'],
  loading: ['Loading…', 'טוען…'],
  retry: ['Try again', 'לנסות שוב'],
  save: ['Save', 'שמירה'],
  saving: ['Saving…', 'שומר…'],
  saved: ['Saved', 'נשמר'],
  cancel: ['Cancel', 'ביטול'],
  create: ['Create', 'יצירה'],
  close: ['Close', 'סגירה'],
  remove: ['Remove', 'הסרה'],
  edit: ['Edit', 'עריכה'],
  open: ['Open', 'פתיחה'],
  copy: ['Copy', 'העתקה'],
  copied: ['Link copied', 'הקישור הועתק'],
  back: ['Back', 'חזרה'],
  optional: ['optional', 'לא חובה'],
  you: ['you', 'את/ה'],
  none: ['None', 'אין'],
  errGeneric: ['Something went wrong. Try again in a moment.', 'משהו השתבש. נסו שוב בעוד רגע.'],
  errNetwork: ['Can’t reach the server. Check your connection.', 'אין חיבור לשרת. בדקו את החיבור לאינטרנט.'],
  errForbidden: ['Your role can’t do this.', 'לתפקיד שלך אין הרשאה לפעולה הזו.'],
  errNotFound: ['We couldn’t find this page.', 'לא מצאנו את העמוד הזה.'],
  notFoundTitle: ['Page not found', 'העמוד לא נמצא'],
  goHome: ['Go to Home', 'לעמוד הבית'],
  confirmRemove: ['Remove {name}?', 'להסיר את {name}?'],
  requiredField: ['Required', 'שדה חובה'],

  // ── Shell: navigation ────────────────────────────────────────
  grpWork: ['Workspace', 'סביבת עבודה'],
  grpCreate: ['Create', 'יצירה'],
  grpDeliver: ['Deliver', 'הפצה'],
  grpAdmin: ['Admin', 'ניהול'],
  nHome: ['Home', 'בית'],
  nInsights: ['Dashboard', 'לוח בקרה'],
  nProjects: ['Projects', 'פרויקטים'],
  nCustomers: ['Customers', 'לקוחות'],
  nBrand: ['Brand kits', 'ערכות מותג'],
  nEpisode: ['Episode', 'פרק'],
  nLive: ['Live studio', 'אולפן חי'],
  nDirector: ['AI director', 'במאי AI'],
  nEditor: ['Editor', 'עורך'],
  nLocalize: ['Languages & voice', 'שפות וקול'],
  nApprovals: ['Approvals', 'אישורים'],
  nPublish: ['Publish & export', 'פרסום וייצוא'],
  nCalendar: ['Calendar', 'לוח פרסומים'],
  nPlayer: ['Players & embeds', 'נגנים והטמעה'],
  nTeam: ['Team', 'צוות'],
  nInteg: ['Integrations', 'חיבורים'],
  mainNav: ['Main navigation', 'ניווט ראשי'],
  skipToContent: ['Skip to content', 'דילוג לתוכן'],

  // ── Shell: header ────────────────────────────────────────────
  search: ['Search projects, clips, people', 'חיפוש פרויקטים, קליפים, אנשים'],
  searchSoon: ['Search arrives in phase P2', 'החיפוש מגיע בשלב P2'],
  online: ['{n} online', '{n} מחוברים'],
  presenceSoon: ['Live presence arrives in phase P2', 'נוכחות בזמן אמת מגיעה בשלב P2'],
  themeTip: ['Theme: {mode}. Click to switch', 'ערכת נושא: {mode}. לחיצה להחלפה'],
  themeDark: ['Dark', 'כהה'],
  themeLight: ['Light', 'בהיר'],
  themeSystem: ['System', 'לפי המערכת'],
  language: ['Language', 'שפה'],
  account: ['Account', 'חשבון'],
  workspace: ['Workspace', 'סביבת עבודה'],
  switchWorkspace: ['Switch workspace', 'החלפת סביבת עבודה'],
  logout: ['Log out', 'התנתקות'],
  aiMode: ['Cloud mode · Pilot 1', 'מצב ענן · פיילוט 1'],
  aiModeSub: ['Media is processed on your Castfeeds server. Only transcript text goes to the AI API.', 'המדיה מעובדת בשרת Castfeeds שלכם. רק טקסט התמלול נשלח ל‑API של ה‑AI.'],

  // ── Coming soon ──────────────────────────────────────────────
  comingSoon: ['Coming soon', 'בקרוב'],
  comingSoonPhase: ['Coming soon · phase {phase}', 'בקרוב · שלב {phase}'],
  comingSoonSub: ['This part of Castfeeds is designed and on the roadmap. Pilot 1 covers upload, drill-down, review and download.', 'החלק הזה של Castfeeds מתוכנן ונמצא במפת הדרכים. פיילוט 1 כולל העלאה, פירוק לסרטונים, בדיקה והורדה.'],

  // ── Auth ─────────────────────────────────────────────────────
  loginTitle: ['Log in to Castfeeds', 'התחברות ל‑Castfeeds'],
  loginSub: ['Record once. Feed every channel.', 'מקליטים פעם אחת. מזינים כל ערוץ.'],
  email: ['Email', 'אימייל'],
  password: ['Password', 'סיסמה'],
  passwordHint: ['At least 10 characters', 'לפחות 10 תווים'],
  loginBtn: ['Log in', 'התחברות'],
  loggingIn: ['Logging in…', 'מתחבר…'],
  resetTitle: ['Choose a new password', 'בחירת סיסמה חדשה'],
  resetSub: ['Castfeeds support sent you this one-time link. Choosing a password signs you out on every other device.', 'צוות התמיכה של Castfeeds שלח לך את הקישור החד-פעמי הזה. בחירת סיסמה מנתקת אותך מכל שאר המכשירים.'],
  newPassword: ['New password', 'סיסמה חדשה'],
  confirmPassword: ['Repeat the new password', 'הקלדת הסיסמה החדשה שוב'],
  passwordsDiffer: ['The two passwords are different', 'שתי הסיסמאות שונות'],
  resetBtn: ['Save and log in', 'שמירה והתחברות'],
  resetting: ['Saving…', 'שומר…'],
  noAccount: ['New here?', 'חדשים כאן?'],
  registerLink: ['Create a workspace', 'יצירת סביבת עבודה'],
  registerTitle: ['Create your workspace', 'יצירת סביבת העבודה שלכם'],
  registerSub: ['One workspace for your studio: customers, projects, brand kits and team.', 'סביבת עבודה אחת לאולפן: לקוחות, פרויקטים, ערכות מותג וצוות.'],
  yourName: ['Your name', 'השם שלך'],
  workspaceName: ['Studio or company name', 'שם האולפן או החברה'],
  registerBtn: ['Create workspace', 'יצירת סביבת עבודה'],
  creating: ['Creating…', 'יוצר…'],
  haveAccount: ['Already have an account?', 'כבר יש לכם חשבון?'],
  inviteTitle: ['Join {workspace}', 'הצטרפות ל‑{workspace}'],
  inviteAs: ['You’re invited as {role}.', 'הוזמנת בתפקיד {role}.'],
  inviteFor: ['This invite is for {email}.', 'ההזמנה נשלחה אל {email}.'],
  inviteExpires: ['Expires {date}', 'בתוקף עד {date}'],
  inviteAccept: ['Accept invite', 'קבלת ההזמנה'],
  inviteAccepting: ['Joining…', 'מצטרף…'],
  inviteNewAccount: ['Create your account to join', 'יצירת חשבון והצטרפות'],
  inviteHaveAccount: ['I already have an account', 'כבר יש לי חשבון'],
  inviteLoggedInAs: ['Logged in as {email}', 'מחובר/ת בתור {email}'],
  inviteWrongEmail: ['This invite was sent to {email}. Log out and log in with that email.', 'ההזמנה נשלחה אל {email}. התנתקו והתחברו עם האימייל הזה.'],
  inviteInvalid: ['This invite is no longer valid', 'ההזמנה כבר לא בתוקף'],
  inviteInvalidSub: ['It may have expired, been used or been revoked. Ask a workspace admin for a new one.', 'ייתכן שפג תוקפה, שכבר השתמשו בה או שבוטלה. בקשו הזמנה חדשה ממנהל סביבת העבודה.'],

  // ── Roles ────────────────────────────────────────────────────
  role_owner: ['Owner', 'בעלים'],
  role_admin: ['Admin', 'מנהל'],
  role_editor: ['Editor', 'עורך'],
  role_producer: ['Producer', 'מפיק'],
  role_viewer: ['Viewer', 'צופה'],
  role_client_reviewer: ['Client reviewer', 'מאשר מטעם הלקוח'],

  // ── Statuses ─────────────────────────────────────────────────
  st_processing: ['Processing', 'בעיבוד'],
  st_review: ['Needs review', 'ממתין לבדיקה'],
  st_client: ['Client review', 'בדיקת לקוח'],
  st_changes: ['Changes requested', 'נדרשים שינויים'],
  st_approved: ['Approved', 'אושר'],
  st_scheduled: ['Scheduled', 'מתוזמן'],
  st_published: ['Published', 'פורסם'],
  st_draft: ['Draft', 'טיוטה'],
  st_failed: ['Failed', 'נכשל'],
  st_proposed: ['AI proposed', 'הצעת AI'],
  st_rejected: ['Rejected', 'נדחה'],
  st_rendering: ['Rendering', 'ברינדור'],

  // ── Home ─────────────────────────────────────────────────────
  goodMorning: ['Good morning, {name}', 'בוקר טוב, {name}'],
  goodAfternoon: ['Good afternoon, {name}', 'צהריים טובים, {name}'],
  goodEvening: ['Good evening, {name}', 'ערב טוב, {name}'],
  homeSub: ['{customers} customers · {projects} active projects · {posts} posts this week', '{customers} לקוחות · {projects} פרויקטים פעילים · {posts} פוסטים השבוע'],
  openCal: ['Open calendar', 'ללוח הפרסומים'],
  newProject: ['New project', 'פרויקט חדש'],
  uploadCardTitle: ['Upload a recorded session', 'העלאת הקלטה'],
  uploadCardSub: ['Cameras and mic tracks from any recorder. We sync, transcribe and drill it down into value and social videos.', 'מצלמות וערוצי מיקרופון מכל מקליט. אנחנו מסנכרנים, מתמללים ומפרקים לסרטוני ערך ולסרטונים לרשתות.'],
  uploadCardBtn: ['Choose a project', 'בחירת פרויקט'],
  liveSub: ['Switch cameras, put cards and logos on screen, mark moments while you record.', 'מחליפים מצלמות, מעלים כרטיסים ולוגואים למסך ומסמנים רגעים תוך כדי הקלטה.'],
  dirSubShort: ['Describe the video you want; the director finds the moments and cuts it.', 'מתארים את הסרטון שרוצים; הבמאי מוצא את הרגעים וחותך.'],
  attention: ['Needs your attention', 'מחכה לך'],
  upNext: ['Up next', 'הפרסומים הבאים'],
  recent: ['Recent episodes, all projects', 'פרקים אחרונים, כל הפרויקטים'],
  noAttention: ['Nothing waiting on you. Nice.', 'שום דבר לא מחכה לך. יפה.'],
  noUpNext: ['No posts scheduled yet.', 'עוד אין פוסטים מתוזמנים.'],
  postInternalReview: ['Internal review', 'בבדיקה פנימית'],
  // Publish & export (prototype v_10publish)
  pubSub: ['Pick videos and channels. Each post waits for its video’s approval and goes out at its time.', 'בוחרים סרטונים וערוצים. כל פוסט מחכה לאישור הסרטון שלו ויוצא בזמן שנקבע.'],
  pubManualNote: ['Posting straight to the platforms comes after their app reviews. Until then, each post appears here on time, and the download pack has everything to post by hand.', 'פרסום ישיר לפלטפורמות יגיע אחרי אישור האפליקציות שלהן. עד אז כל פוסט מופיע כאן בזמן, וחבילת ההורדה כוללת את כל מה שצריך לפרסום ידני.'],
  pubAutoNote: ['Posts on connected channels ({platforms}) go out by themselves at their time, once their video is approved. Other channels: post by hand from the download pack.', 'פוסטים בערוצים מחוברים ({platforms}) יוצאים לבד בזמן שנקבע, אחרי שהסרטון אושר. בשאר הערוצים מפרסמים ידנית מחבילת ההורדה.'],
  pubConnectLink: ['Connect channels', 'חיבור ערוצים'],
  pubVideos: ['Videos', 'סרטונים'],
  pubPicked: ['{n} picked', '{n} נבחרו'],
  pubNoVideos: ['No videos in approval yet. Send videos to approval from an episode first.', 'עדיין אין סרטונים באישור. קודם שולחים סרטונים לאישור מתוך פרק.'],
  pubChannels: ['Channels', 'ערוצים'],
  pubChannelCount: ['{n} selected', '{n} נבחרו'],
  pubWhen: ['When', 'מתי'],
  pubQueue: ['Add to queue', 'הוספה לתור'],
  pubAt: ['Schedule', 'תזמון'],
  pubAtLabel: ['Date and time (same on every channel)', 'תאריך ושעה (זהים בכל הערוצים)'],
  pubQueueHint: ['Each video gets the next free slot in its project’s cadence ({tz}).', 'כל סרטון מקבל את החריץ הפנוי הבא לפי תדירות הפרויקט ({tz}).'],
  pubSummary: ['Summary', 'סיכום'],
  pubCount: ['{videos} videos × {channels} channels = {posts} posts', '{videos} סרטונים × {channels} ערוצים = {posts} פוסטים'],
  pubHeldNote: ['{n} of these videos are still in approval. Their posts wait and go out only once approved.', '{n} מהסרטונים האלה עדיין באישור. הפוסטים שלהם יחכו ויצאו רק אחרי האישור.'],
  pubPack: ['Download pack', 'הורדת חבילה'],
  pubSchedule: ['Schedule {n} posts', 'תזמון {n} פוסטים'],
  pubDone: ['{n} posts scheduled.', '{n} פוסטים תוזמנו.'],
  pubDoneHeld: ['{n} posts scheduled; {held} wait for approval.', '{n} פוסטים תוזמנו; {held} מחכים לאישור.'],
  pubPosts: ['Scheduled posts', 'פוסטים מתוזמנים'],
  pubPostsSub: ['Last 7 days and next 30', '7 הימים האחרונים ו-30 הקרובים'],
  postWaitsTip: ['Goes out once its video is approved.', 'יתפרסם אחרי שהסרטון יאושר.'],
  noRecent: ['No episodes yet. Create a project and add its first episode.', 'עוד אין פרקים. צרו פרויקט והוסיפו לו פרק ראשון.'],
  attnChanges: ['{project} · changes requested', '{project} · נדרשים שינויים'],
  attnReview: ['{project} · ready for your review', '{project} · מוכן לבדיקה שלך'],
  attnClient: ['{project} · waiting for the client', '{project} · ממתין ללקוח'],
  today: ['Today', 'היום'],
  tomorrow: ['Tomorrow', 'מחר'],

  // ── Projects ─────────────────────────────────────────────────
  projSub: ['Each project has its own brand kit, channels, approval flow and team.', 'לכל פרויקט ערכת מותג, ערוצים, תהליך אישור וצוות משלו.'],
  colProject: ['Project', 'פרויקט'],
  colCustomer: ['Customer', 'לקוח'],
  colEps: ['Eps.', 'פרקים'],
  colNext: ['Next post', 'פרסום הבא'],
  colTeam: ['Team', 'צוות'],
  all: ['All customers', 'כל הלקוחות'],
  brandKit: ['Brand kit', 'ערכת מותג'],
  editKit: ['Edit', 'עריכה'],
  channels: ['Channels', 'ערוצים'],
  approvalFlow: ['Approval flow', 'תהליך אישור'],
  cadence: ['Cadence', 'תדירות'],
  episodes: ['Episodes', 'פרקים'],
  addEpisode: ['+ Add episode', '+ הוספת פרק'],
  noProjects: ['No projects yet. A project belongs to a customer and holds its episodes.', 'עוד אין פרויקטים. פרויקט שייך ללקוח ומכיל את הפרקים שלו.'],
  noProjectsForCustomer: ['This customer has no projects yet.', 'ללקוח הזה עוד אין פרויקטים.'],
  noEpisodes: ['No episodes yet.', 'עוד אין פרקים.'],
  selectProject: ['Select a project to see its details.', 'בחרו פרויקט כדי לראות את הפרטים.'],
  editProject: ['Edit project', 'עריכת פרויקט'],
  projTitle: ['Project name', 'שם הפרויקט'],
  projType: ['Type', 'סוג'],
  projTypePh: ['Weekly video podcast', 'פודקאסט וידאו שבועי'],
  projLanguage: ['Main language', 'שפה עיקרית'],
  projCadence: ['Cadence', 'תדירות'],
  projCadencePh: ['1 episode → 5 value → 15 social / week', 'פרק 1 ← 5 ערך ← 15 לרשתות / שבוע'],
  projInternalReview: ['Producer reviews before the customer', 'מפיק בודק לפני הלקוח'],
  projVocabulary: ['Vocabulary for transcription', 'אוצר מילים לתמלול'],
  projVocabularyHint: ['Names and terms, comma separated. Helps the transcript spell them right.', 'שמות ומונחים, מופרדים בפסיקים. עוזר לתמלול לאיית אותם נכון.'],
  needCustomerFirst: ['Create a customer first. Every project belongs to one.', 'צרו קודם לקוח. כל פרויקט שייך ללקוח.'],
  flowEditor: ['Editor', 'עורך'],
  flowProducer: ['Producer', 'מפיק'],
  flowPublish: ['Publish', 'פרסום'],
  lang_he: ['Hebrew', 'עברית'],
  lang_en: ['English', 'אנגלית'],
  lang_ar: ['Arabic', 'ערבית'],
  lang_ru: ['Russian', 'רוסית'],
  epNumber: ['No.', 'מס׳'],
  epTitle: ['Episode title', 'שם הפרק'],
  epRecordedAt: ['Recorded on', 'תאריך הקלטה'],
  epGuests: ['Guests', 'אורחים'],
  epGuestsHint: ['Comma separated', 'מופרדים בפסיקים'],
  epCountShort: ['{n} eps.', '{n} פרקים'],

  // ── Customers ────────────────────────────────────────────────
  custSub: ['Brand, reviewers and approval rules live with the customer. Projects inherit them.', 'המותג, המאשרים וכללי האישור שמורים אצל הלקוח. הפרויקטים יורשים אותם.'],
  newCustomer: ['New customer', 'לקוח חדש'],
  custName: ['Customer name', 'שם הלקוח'],
  custKind: ['Kind', 'סוג'],
  custKindPh: ['Finance · monthly retainer', 'פיננסים · ריטיינר חודשי'],
  contactName: ['Contact name', 'איש קשר'],
  contactEmail: ['Contact email', 'אימייל איש קשר'],
  noCustomers: ['No customers yet.', 'עוד אין לקוחות.'],
  pendingApprovals: ['Waiting for this customer', 'ממתין ללקוח הזה'],
  reviewers: ['Reviewers', 'מאשרים'],
  noReviewers: ['No reviewers yet. Add the people who approve for this customer.', 'עוד אין מאשרים. הוסיפו את מי שמאשר מטעם הלקוח.'],
  addReviewer: ['+ Add reviewer', '+ הוספת מאשר'],
  reviewerName: ['Name', 'שם'],
  reviewerLabel: ['Label', 'תווית'],
  reviewerLabelPh: ['Final approver', 'מאשר סופי'],
  approvalRules: ['Approval rules', 'כללי אישור'],
  ruleReq: ['Customer must approve before publishing', 'הלקוח חייב לאשר לפני פרסום'],
  ruleReqSub: ['Scheduled posts wait in “Client review” until approved.', 'פוסטים מתוזמנים ממתינים ב„בדיקת לקוח” עד לאישור.'],
  ruleAuto: ['Auto-approve after {hours} h of silence', 'אישור אוטומטי אחרי {hours} שעות בלי תגובה'],
  ruleAutoSub: ['Reviewers get a reminder at {half} h.', 'המאשרים מקבלים תזכורת אחרי {half} שעות.'],
  ruleTr: ['Let reviewers edit the transcript', 'המאשרים יכולים לערוך את התמלול'],
  ruleTrSub: ['Text fixes only. Cuts stay with your team.', 'תיקוני טקסט בלבד. החיתוכים נשארים אצל הצוות.'],
  ruleFull: ['Full episodes need a second approver', 'פרק מלא דורש מאשר נוסף'],
  ruleFullSub: ['Value and social videos need one.', 'סרטוני ערך וסרטונים לרשתות דורשים אחד.'],
  portalLink: ['Client review link (no login, expires in 14 days)', 'קישור לבדיקת הלקוח (בלי התחברות, בתוקף 14 יום)'],
  copyPortal: ['Copy review link', 'העתקת קישור לבדיקה'],
  createPortal: ['Create review link', 'יצירת קישור לבדיקה'],
  portalOnce: ['Copy it now. For security we show each link only once.', 'העתיקו עכשיו. מטעמי אבטחה כל קישור מוצג פעם אחת בלבד.'],
  portalLinks: ['Review links', 'קישורים לבדיקה'],
  noPortalLinks: ['No review links yet.', 'עוד אין קישורים לבדיקה.'],
  linkActive: ['Active · expires {date}', 'פעיל · בתוקף עד {date}'],
  linkInactive: ['Expired or revoked', 'פג תוקף או בוטל'],
  linkViews: ['{n} views', '{n} צפיות'],
  revoke: ['Revoke', 'ביטול'],
  uMin: ['Minutes processed', 'דקות שעובדו'],
  uRend: ['Videos rendered', 'סרטונים שרונדרו'],
  uStore: ['Storage', 'אחסון'],

  // ── Brand kits ───────────────────────────────────────────────
  kColors: ['Colours', 'צבעים'],
  kc_primary: ['Primary', 'ראשי'],
  kc_ink: ['Ink', 'דיו'],
  kc_paper: ['Paper', 'נייר'],
  kc_secondary: ['Secondary', 'משני'],
  kc_highlight: ['Highlight', 'הדגשה'],
  kHeading: ['Heading font', 'גופן כותרות'],
  kBody: ['Body font', 'גופן טקסט'],
  kCaptions: ['Captions', 'כתוביות'],
  kWords: ['Words per line', 'מילים בשורה'],
  kPos: ['Position', 'מיקום'],
  kCapSize: ['Caption size', 'גודל כתוביות'],
  capSize_auto: ['Auto', 'אוטומטי'],
  capSize_m: ['Standard', 'רגיל'],
  capSize_l: ['Large', 'גדול'],
  capSizeAutoTip: ['Large for social shorts, standard elsewhere', 'גדול בסרטונים קצרים לרשתות, רגיל בכל השאר'],
  pos_top: ['Top', 'למעלה'],
  pos_middle: ['Middle', 'אמצע'],
  pos_low: ['Low', 'למטה'],
  cap_bold_pop: ['Bold pop', 'פופ בולט'],
  cap_boxed: ['Boxed', 'מסגרת'],
  cap_clean: ['Clean', 'נקי'],
  cap_karaoke: ['Karaoke', 'קריוקי'],
  capSample: ['word', 'מילה'],
  kLower: ['Lower-third', 'כתובית שם'],
  lt_bar: ['Bar', 'פס'],
  lt_tag: ['Tag', 'תגית'],
  lt_minimal: ['Minimal', 'מינימלי'],
  kMusic: ['Default music mood', 'אווירת מוזיקה'],
  mood_upbeat: ['Upbeat', 'אנרגטי'],
  mood_warm: ['Warm', 'חם'],
  mood_corporate: ['Corporate', 'עסקי'],
  mood_cinematic: ['Cinematic', 'קולנועי'],
  mood_lofi: ['Lo‑fi', 'לו‑פיי'],
  kWatermark: ['Logo bug', 'לוגו פינה'],
  corner_top_left: ['Top left', 'למעלה משמאל'],
  corner_top_right: ['Top right', 'למעלה מימין'],
  corner_bottom_left: ['Bottom left', 'למטה משמאל'],
  corner_bottom_right: ['Bottom right', 'למטה מימין'],
  kAppliesTo: ['Applies to every render in this kit.', 'חל על כל רינדור בערכה הזו.'],
  kEndCard: ['End card text', 'טקסט כרטיס סיום'],
  kDisclaimer: ['Legal disclaimer', 'הבהרה משפטית'],
  kDisclaimerPh: ['Shown at the end of every video when set', 'מוצג בסוף כל סרטון כשמוגדר'],
  kLogos: ['Logos and stings', 'לוגואים ופתיחים'],
  kLogosSoon: ['Logo upload arrives with asset storage (phase P1 week 2).', 'העלאת לוגואים מגיעה עם אחסון הנכסים (שלב P1, שבוע 2).'],
  slotPrimary: ['Primary logo', 'לוגו ראשי'],
  slotMark: ['Mark / bug', 'סמל / פינה'],
  slotIntro: ['Intro sting', 'פתיח'],
  slotEnd: ['End card', 'כרטיס סיום'],
  inherited: ['Inherited', 'בירושה'],
  overridden: ['Overrides customer', 'דורס את הלקוח'],
  reset: ['Reset', 'איפוס'],
  livePreview: ['Live preview', 'תצוגה חיה'],
  sampleHook: ['Why studios lose 3 days per episode', 'למה אולפנים מפסידים 3 ימים לפרק'],
  sampleName: ['Ido Shalev', 'עידו שלו'],
  sampleRole: ['Producer, Triangle', 'מפיק, טריאנגל'],
  sampleCaption: ['three days per episode', 'שלושה ימים לכל פרק'],
  endCard: ['New episode every Sunday', 'פרק חדש בכל יום ראשון'],
  kitUsedBy: ['Edits here re-render open drafts that use this kit. Approved videos are never changed.', 'שינויים כאן מרנדרים מחדש טיוטות פתוחות שמשתמשות בערכה. סרטונים מאושרים לא משתנים.'],
  kitCustomer: ['customer', 'לקוח'],
  kitProject: ['project', 'פרויקט'],
  kitScopeCustomer: ['Customer kit · projects inherit from here', 'ערכת לקוח · הפרויקטים יורשים ממנה'],
  kitScopeProject: ['Project kit · {customer}', 'ערכת פרויקט · {customer}'],
  kitInherits: ['Inherits from {name} · {n} override(s)', 'יורשת מ‑{name} · {n} שינויים'],
  kitReadOnly: ['Your role can view this kit but not change it.', 'לתפקיד שלך יש הרשאת צפייה בלבד בערכה הזו.'],
  noKits: ['No brand kits yet. Each customer gets one automatically.', 'עוד אין ערכות מותג. כל לקוח מקבל ערכה באופן אוטומטי.'],
  contrastWarn: ['Caption contrast {ratio}:1 is below 4.5:1. Viewers may struggle to read it.', 'ניגודיות הכתוביות {ratio}:1 נמוכה מ‑4.5:1. ייתכן שיהיה קשה לקרוא.'],

  // ── Team ─────────────────────────────────────────────────────
  teamSub: ['Members, roles and who can approve, publish or spend on AI generation.', 'חברי צוות, תפקידים, ומי יכול לאשר, לפרסם או להוציא כסף על יצירת AI.'],
  invite: ['Invite', 'הזמנה'],
  sendInvite: ['Send invite', 'שליחת הזמנה'],
  inviteScope: ['Access', 'גישה'],
  allProjects: ['All projects', 'כל הפרויקטים'],
  externalFreelancer: ['External freelancer', 'פרילנסר חיצוני'],
  external: ['external', 'חיצוני'],
  inviteCreated: ['Invite created. Email delivery is coming; send this link yourself meanwhile:', 'ההזמנה נוצרה. שליחה במייל בדרך; בינתיים שלחו את הקישור הזה בעצמכם:'],
  pendingInvites: ['Pending invites', 'הזמנות ממתינות'],
  noPendingInvites: ['No pending invites.', 'אין הזמנות ממתינות.'],
  inviteExpired: ['expired', 'פג תוקף'],
  member: ['Member', 'חבר צוות'],
  role: ['Role', 'תפקיד'],
  access: ['Access', 'גישה'],
  lastActive: ['Last active', 'פעילות אחרונה'],
  never: ['Never', 'אף פעם'],
  permissions: ['Role permissions', 'הרשאות לפי תפקיד'],
  cap_view: ['View projects', 'צפייה בפרויקטים'],
  cap_edit: ['Edit videos', 'עריכת סרטונים'],
  cap_ai: ['AI generation (paid)', 'יצירת AI (בתשלום)'],
  cap_internal: ['Approve internally', 'אישור פנימי'],
  cap_customer: ['Approve as customer', 'אישור כלקוח'],
  cap_comment: ['Comment', 'תגובות'],
  cap_publish: ['Publish and schedule', 'פרסום ותזמון'],
  cap_kits: ['Brand kits', 'ערכות מותג'],
  cap_members: ['Members and billing', 'חברי צוות וחיוב'],
  removeMember: ['Remove from workspace', 'הסרה מסביבת העבודה'],

  // ── Episode: upload ──────────────────────────────────────────
  uploadTitle: ['Upload media', 'העלאת מדיה'],
  uploadSub: ['Add every camera ISO and mic track from the session. Large files upload in parts and resume after a drop.', 'הוסיפו את כל קובצי ה‑ISO של המצלמות ואת ערוצי המיקרופון. קבצים גדולים עולים בחלקים וממשיכים מאותה נקודה אחרי ניתוק.'],
  dropHere: ['Drop video and audio files here, or', 'גררו לכאן קובצי וידאו ואודיו, או'],
  browse: ['choose files', 'בחרו קבצים'],
  fileKind: ['Kind', 'סוג'],
  kind_camera: ['Camera', 'מצלמה'],
  kind_mic: ['Mic', 'מיקרופון'],
  kind_mix: ['Mix', 'מיקס'],
  kind_program: ['Program', 'פרוגרם'],
  kind_screen: ['Screen', 'מסך'],
  kind_upload: ['Other', 'אחר'],
  filePerson: ['Person', 'דובר/ת'],
  filePersonPh: ['Who is on this track', 'מי בערוץ הזה'],
  fileLabel: ['Label', 'תווית'],
  uploadAll: ['Upload {n} file(s)', 'העלאת {n} קבצים'],
  uploadPause: ['Pause', 'השהיה'],
  uploadResume: ['Resume', 'המשך'],
  uploadRetry: ['Retry', 'ניסיון חוזר'],
  uploadDone: ['Uploaded', 'הועלה'],
  uploadWaiting: ['Waiting', 'ממתין'],
  uploadFailed: ['Upload failed', 'ההעלאה נכשלה'],
  uploadResumable: ['Paused · re-add the same file to resume', 'מושהה · הוסיפו שוב את אותו קובץ כדי להמשיך'],
  uploadResumed: ['Resuming from part {n}', 'ממשיך מחלק {n}'],
  uploadSpeed: ['{speed}/s · {eta} left', '{speed}/ש׳ · נותרו {eta}'],
  uploadLeaveWarn: ['Uploads are running. Leave anyway?', 'יש העלאות פעילות. לצאת בכל זאת?'],
  startProcessing: ['Start processing', 'התחלת עיבוד'],
  startingProcessing: ['Starting…', 'מתחיל…'],
  needOneFile: ['Upload at least one file to start processing.', 'העלו לפחות קובץ אחד כדי להתחיל בעיבוד.'],
  // ── Multitrack mic files (amendment 24) ─────────────────────
  mtTitle: ['{file}: {n} channels, one mic per channel?', '{file}: {n} ערוצים, מיקרופון אחד לכל ערוץ?'],
  mtSub: ['This file holds several mics. Pick who is on each channel and skip the rest; each chosen channel becomes its own mic, so the transcript knows who said what.', 'בקובץ הזה כמה מיקרופונים. בחרו מי מדבר בכל ערוץ ודלגו על השאר; כל ערוץ שנבחר הופך למיקרופון נפרד, כך שהתמלול יודע מי אמר מה.'],
  mtChannel: ['Channel', 'ערוץ'],
  mtChannelN: ['Ch {n}', 'ערוץ {n}'],
  mtLevel: ['Level', 'עוצמה'],
  mtLevelOf: ['Level of channel {n}: {rms} dBFS average, {peak} dBFS peak', 'עוצמת ערוץ {n}: ממוצע {rms} dBFS, שיא {peak} dBFS'],
  mtPreview: ['Listen', 'האזנה'],
  mtPreviewOf: ['Listen to 10 seconds of channel {n}', 'האזנה ל-10 שניות מערוץ {n}'],
  mtNoPreview: ['Preview not available for studio-computer files', 'אין האזנה לקבצים שעל מחשב באולפן'],
  mtSilentWhy: ['Measured {rms} dBFS on average: a guest who barely speaks can look silent, so listen before you skip', 'נמדד ממוצע של {rms} dBFS: אורח/ת שכמעט לא מדבר/ת יכול/ה להיראות שקט/ה, אז האזינו לפני הדילוג'],
  mtProcessingStarted: ['Processing has started, so the channels can no longer change.', 'העיבוד כבר התחיל, ולכן אי אפשר לשנות את הערוצים.'],
  mtPerson: ['Who is on it', 'מי מדבר'],
  mtSkip: ['Skip', 'דילוג'],
  mtSilent: ['Silent', 'שקט'],
  mtDuplicate: ['Same as Ch {n}', 'זהה לערוץ {n}'],
  mtMix: ['Mix of the others', 'מיקס של האחרים'],
  mtPreset: ['Device', 'מכשיר'],
  mtPresetNone: ['Any recorder', 'כל מכשיר הקלטה'],
  mtPresetHint: ['Suggests channel names only. Silence and duplicates are detected from the sound, for any recorder.', 'מציע שמות לערוצים בלבד. ערוצים שקטים וכפולים מזוהים לפי הצליל, בכל מכשיר הקלטה.'],
  mtSplit: ['Make {n} mics', 'יצירת {n} מיקרופונים'],
  mtSplitting: ['Splitting…', 'מפצל…'],
  mtMixdown: ['Keep as one mixed mic', 'להשאיר כמיקרופון מעורבב אחד'],
  mtMixdownHint: ['Everyone is on one track: the transcript will not know who is talking.', 'כולם בערוץ אחד: התמלול לא יידע מי מדבר.'],
  mtNeedPerson: ['Name the person on every channel you keep.', 'כתבו את שם הדובר/ת בכל ערוץ שנשאר.'],
  mtNeedOne: ['Keep at least one channel, or keep the file as one mixed mic.', 'השאירו לפחות ערוץ אחד, או השאירו את הקובץ כמיקרופון מעורבב אחד.'],
  mtDoneSplit: ['Split into {n} mics: {names}', 'פוצל ל-{n} מיקרופונים: {names}'],
  mtDoneMixdown: ['Kept as one mixed mic', 'נשאר כמיקרופון מעורבב אחד'],
  mtChange: ['Change', 'שינוי'],
  mtPending: ['Decide the channels of every multitrack file before you start processing.', 'החליטו על הערוצים של כל קובץ רב-ערוצי לפני שמתחילים בעיבוד.'],
  mtNoSummary: ['Levels are not available for this file type; listen in your recorder’s app if you are unsure.', 'אין נתוני עוצמה לסוג הקובץ הזה; אם לא בטוחים, האזינו באפליקציה של מכשיר ההקלטה.'],
  mtChTag: ['Ch {n}', 'ערוץ {n}'],
  uploadFormatHint: ['Pilot 1 accepts H.264/HEVC video and WAV/MP3/M4A audio. ProRes uploads are very large.', 'פיילוט 1 מקבל וידאו H.264/HEVC ואודיו WAV/MP3/M4A. קבצי ProRes גדולים מאוד.'],

  // ── Episode: processing ──────────────────────────────────────
  processing: ['Processing', 'עיבוד'],
  stage_ingest: ['Import & proxies', 'ייבוא ותצוגות'],
  stage_sync: ['Sync ±1 frame', 'סנכרון ±1 פריים'],
  stage_transcribe: ['Transcribe', 'תמלול'],
  stage_analyze: ['Understand & drill down', 'הבנה ופירוק'],
  stage_render: ['Render', 'רינדור'],
  stage_export: ['Export', 'ייצוא'],
  stageQueued: ['Queued', 'בתור'],
  stageRunning: ['Running', 'רץ'],
  stageDone: ['Done', 'הסתיים'],
  stageFailed: ['Failed', 'נכשל'],
  stageCancelled: ['Cancelled', 'בוטל'],
  costSoFar: ['Cost so far ${cost}', 'עלות עד כה {cost}$'],
  overallProgress: ['Overall progress', 'התקדמות כללית'],
  syncClap: ['Sync needs a hand: mark one clap or shared moment.', 'הסנכרון צריך עזרה: סמנו מחיאת כף אחת או רגע משותף.'],
  syncConfidence: ['{source}: confidence {pct}%', '{source}: ביטחון {pct}%'],
  retryProcessing: ['Retry processing', 'הפעלת העיבוד מחדש'],
  noSession: ['No media yet. Upload the session’s files to start.', 'עוד אין מדיה. העלו את קובצי ההקלטה כדי להתחיל.'],
  sessionFailed: ['Processing stopped: {error}', 'העיבוד נעצר: {error}'],
  uploadMore: ['Upload media', 'העלאת מדיה'],

  // ── Episode: drill-down ──────────────────────────────────────
  drilldown: ['Drill-down', 'פירוק לסרטונים'],
  recentEpisodesSub: ['The latest episodes across every project you can see.', 'הפרקים האחרונים מכל הפרויקטים שיש לך גישה אליהם.'],
  approveTop: ['Approve all ≥ {score}', 'אישור כל מה שמעל {score}'],
  approve: ['Approve', 'אישור'],
  reject: ['Reject', 'דחייה'],
  approved: ['Approved', 'אושר'],
  play: ['Play', 'ניגון'],
  playing: ['Playing {title}', 'מנגן את {title}'],
  download: ['Download', 'הורדה'],
  downloadSrt: ['Captions (.srt)', 'כתוביות (‎.srt)'],
  downloadPack: ['Download pack ({n})', 'הורדת חבילה ({n})'],
  packPreparing: ['Preparing ZIP…', 'מכין ZIP…'],
  packReady: ['ZIP ready', 'ה‑ZIP מוכן'],
  packHint: ['MP4 + SRT + caption text for every approved video', 'MP4 + SRT + טקסט כיתוב לכל סרטון מאושר'],
  rerender: ['Re-render', 'רינדור מחדש'],
  tFull: ['Full episode', 'פרק מלא'],
  tFullSub: ['Clean cut of the whole conversation for YouTube and Spotify.', 'גרסה נקייה של כל השיחה ליוטיוב ולספוטיפיי.'],
  tValue: ['Value videos', 'סרטוני ערך'],
  tValueSub: ['60–240 s, one topic each, stands alone. Calm pace, chapter-style.', '60–240 שנ׳, נושא אחד לכל סרטון, עומד בפני עצמו. קצב רגוע.'],
  tSocial: ['Social videos', 'סרטונים לרשתות'],
  tSocialSub: ['15–50 s cut from the selected value video. Hook first, music, faster cuts.', '15–50 שנ׳ מתוך סרטון הערך שנבחר. פתיח חזק, מוזיקה, חיתוכים מהירים.'],
  videosCount: ['{n} videos', '{n} סרטונים'],
  socialCount: ['{n} social', '{n} לרשתות'],
  targetLen: ['Target length', 'אורך יעד'],
  howMany: ['How many', 'כמות'],
  format: ['Format', 'פורמט'],
  hookStyle: ['Hook', 'פתיח'],
  music: ['Music', 'מוזיקה'],
  pace: ['Pace', 'קצב'],
  hook_question: ['Question', 'שאלה'],
  hook_bold_claim: ['Bold claim', 'טענה נועזת'],
  hook_number: ['Number', 'מספר'],
  hook_cold_open: ['Cold open', 'פתיחה קרה'],
  smood_upbeat: ['Upbeat', 'אנרגטי'],
  smood_hype: ['Hype', 'היי‑פ'],
  smood_lofi: ['Lo‑fi', 'לו‑פיי'],
  smood_none: ['None', 'בלי'],
  pace_calm: ['Calm', 'רגוע'],
  pace_punchy: ['Punchy', 'מהיר'],
  pace_hyper: ['Hyper', 'מהיר מאוד'],
  regen: ['Regenerate tier', 'יצירה מחדש'],
  regenRun: ['Regenerating…', 'יוצר מחדש…'],
  regenKeeps: ['Approved and human-edited videos are kept.', 'סרטונים מאושרים וסרטונים שנערכו ידנית נשמרים.'],
  noDrilldown: ['The drill-down appears here when processing finishes.', 'הפירוק לסרטונים יופיע כאן כשהעיבוד יסתיים.'],
  selectValue: ['Select a value video to see its social cuts.', 'בחרו סרטון ערך כדי לראות את הסרטונים לרשתות שנחתכו ממנו.'],
  why: ['Why', 'למה'],
  score: ['Score', 'ציון'],
  renderPending: ['Render pending', 'ממתין לרינדור'],
  renderFailed: ['The last render failed. Try Re-render.', 'הרינדור האחרון נכשל. נסו רינדור מחדש.'],
  postCopy: ['Post text', 'טקסט לפוסט'],
  cantPlay: ['This file can’t play in the browser. Download it instead.', 'אי אפשר לנגן את הקובץ בדפדפן. אפשר להוריד אותו.'],
  openTranscript: ['Transcript', 'תמלול'],
  episodeNotFound: ['Episode not found in this project.', 'הפרק לא נמצא בפרויקט הזה.'],

  // ── Transcript ───────────────────────────────────────────────
  transcriptTitle: ['Transcript', 'תמלול'],
  transcriptSub: ['Click a word to fix its spelling. Saving creates a new version and re-renders the drafts that use it.', 'לחיצה על מילה מאפשרת לתקן את האיות. שמירה יוצרת גרסה חדשה ומרנדרת מחדש את הטיוטות שמשתמשות בה.'],
  transcriptVersion: ['Version {n}', 'גרסה {n}'],
  speaker: ['Speaker {n}', 'דובר {n}'],
  wordFix: ['Fix word', 'תיקון מילה'],
  pendingFixes: ['{n} unsaved fix(es)', '{n} תיקונים שלא נשמרו'],
  saveFixes: ['Save and re-render', 'שמירה ורינדור מחדש'],
  discardFixes: ['Discard', 'ביטול השינויים'],
  // Cut toggles and clips from a range (plan §16 item 7)
  cutEdit: ['Edit cut', 'עריכת החיתוך'],
  cutModeWords: ['Cut words', 'חיתוך מילים'],
  cutModeRange: ['Mark a clip', 'סימון קליפ'],
  cutHint: ['Click a word to cut it or bring it back. Struck words are cut.', 'לוחצים על מילה כדי לחתוך אותה או להחזיר אותה. מילים מחוקות נחתכו.'],
  cutLocked: ['Approved videos keep their cut. Mark a clip to make a new one from it.', 'סרטון שאושר שומר על החיתוך שלו. אפשר לסמן ממנו קליפ חדש.'],
  cutNoWords: ['No transcript words in this video yet.', 'עדיין אין מילים מהתמלול בסרטון הזה.'],
  cutByAi: ['Cut automatically', 'נחתך אוטומטית'],
  cutByYou: ['Cut by a person', 'נחתך ידנית'],
  cutKept: ['Brought back', 'הוחזר'],
  cutPending: ['{n} cut changes', '{n} שינויים בחיתוך'],
  cutSave: ['Save and re-render', 'שמירה ורינדור מחדש'],
  cutSaved: ['Saved as version {n}. It re-renders and goes back through approval.', 'נשמר כגרסה {n}. הסרטון ירונדר מחדש ויחזור לאישור.'],
  rangeHint: ['Click the first and the last word of the new clip.', 'לוחצים על המילה הראשונה ועל המילה האחרונה של הקליפ החדש.'],
  rangeSel: ['Clip {from}–{to}', 'קליפ {from}–{to}'],
  makeClip: ['Make clip', 'יצירת קליפ'],
  clipMade: ['Clip “{title}” created. It is rendering.', 'הקליפ „{title}” נוצר ונמצא ברינדור.'],
  clipsTitle: ['Your clips', 'הקליפים שלכם'],
  clipsSub: ['Made from marked ranges', 'נוצרו מטווחים שסימנתם'],
  wordDeleted: ['Removed from the video', 'הוסר מהסרטון'],
  transcriptStale: ['Someone saved a newer version. We loaded it; apply your fixes again.', 'מישהו שמר גרסה חדשה יותר. טענו אותה; החילו שוב את התיקונים.'],
  lowConfidence: ['Low confidence', 'ביטחון נמוך'],
  noTranscript: ['The transcript appears here after the Transcribe stage.', 'התמלול יופיע כאן אחרי שלב התמלול.'],
  downloadTranscriptSrt: ['Download .srt', 'הורדת ‎.srt'],

  // ── Episode: Fix & enhance (WS F, plan §10) ─────────────────
  fxTitle: ['Fix & enhance', 'תיקון ושיפור'],
  fxSub: ['We checked every camera and mic. Safe fixes are already on; switch any of them off and the drafts re-render. Your original files never change.', 'בדקנו כל מצלמה וכל מיקרופון. התיקונים הבטוחים כבר פועלים; אפשר לכבות כל אחד מהם, והטיוטות ירונדרו מחדש. קובצי המקור לא משתנים.'],
  fxSummary: ['{on} of {total} fixes on', '{on} מתוך {total} תיקונים פועלים'],
  fxNeedsLook: ['{n} can’t be fixed automatically', '{n} לא ניתנים לתיקון אוטומטי'],
  fxShow: ['Show fixes', 'הצגת התיקונים'],
  fxHide: ['Hide fixes', 'הסתרת התיקונים'],
  fxNoIssues: ['No problems found.', 'לא מצאנו בעיות.'],
  fxAnalysing: ['Still checking this source…', 'עדיין בודקים את המקור הזה…'],
  fxReadOnly: ['Your role can see these fixes but not change them.', 'התפקיד שלך מאפשר לראות את התיקונים, אבל לא לשנות אותם.'],
  fxEdited: ['Edited', 'נערך ידנית'],
  fxRecommended: ['As recommended', 'לפי ההמלצה'],
  fxToggle: ['Fix: {issue}', 'תיקון: {issue}'],
  fxOff: ['Off', 'כבוי'],
  fxOn: ['On', 'פועל'],
  fxCantRepair: ['Can’t be repaired automatically', 'אי אפשר לתקן אוטומטית'],
  fxRefCamera: ['the reference camera', 'מצלמת הייחוס'],
  fxDarker: ['{name} is {pct}% darker than {ref}', '{name}: התמונה כהה ב־{pct}% לעומת {ref}'],
  fxBrighter: ['{name} is {pct}% brighter than {ref}', '{name}: התמונה בהירה ב־{pct}% לעומת {ref}'],
  fxColourDiff: ['{name}’s colours don’t match {ref}', '{name}: הצבעים שונים מאלה של {ref}'],
  fxMatched: ['Matched', 'הותאמה'],
  fxMatchedColours: ['Matched', 'הותאמו'],
  fxInterlaced: ['Interlaced video', 'וידאו באינטרלייס'],
  fxDeinterlaced: ['Deinterlaced', 'הומר לפרוגרסיבי'],
  fxGrainy: ['Grainy picture', 'גרעיניות בתמונה'],
  fxGrainCleaned: ['Cleaned', 'נוקתה'],
  fxDark: ['Dark picture', 'תמונה חשוכה'],
  fxBrightened: ['Brightened', 'הובהרה'],
  fxCast: ['White balance is off', 'איזון הלבן לא מדויק'],
  fxBalanced: ['Balanced', 'תוקן'],
  fxShake: ['Handheld shake', 'רעידות של צילום ביד'],
  fxStabilised: ['Stabilised', 'יוצבו'],
  fxVfr: ['Variable frame rate', 'קצב פריימים משתנה'],
  fxConformed: ['Conformed to {fps} fps', 'הוסב לקצב קבוע של {fps} פריימים לשנייה'],
  fxBlown: ['{pct}% of the picture is blown out or crushed', '{pct}% מהתמונה שרוף או שחור לגמרי'],
  fxHum: ['{name}: {hz} Hz hum', '{name}: זמזום חשמל ב־{hz} הרץ'],
  fxHumRemoved: ['Removed', 'הוסר'],
  fxNoise: ['Background noise', 'רעשי רקע'],
  fxNoiseCleaned: ['Cleaned ({provider})', 'נוקו ({provider})'],
  fxRumble: ['Low rumble below {hz} Hz', 'המהום נמוך מתחת ל־{hz} הרץ'],
  fxFiltered: ['Filtered', 'סונן'],
  fxClicks: ['Clicks and pops', 'קליקים ופיצוצים'],
  fxClicksRemoved: ['Removed', 'הוסרו'],
  fxQuieter: ['Quieter than the other mics', 'שקט יותר משאר המיקרופונים'],
  fxLouder: ['Louder than the other mics', 'חזק יותר משאר המיקרופונים'],
  fxLevel: ['Loudness {lufs} LUFS', 'עוצמת הקול {lufs} LUFS'],
  fxLevelled: ['Levelled', 'העוצמה הושוותה'],
  fxClipping: ['{name} clips in {pct}% of samples', '{name}: עיוות (clipping) ב־{pct}% מהדגימות'],
  fxk_deinterlace: ['Deinterlace', 'המרה לפרוגרסיבי'],
  fxk_colour_lut: ['Match to the reference camera', 'התאמה למצלמת הייחוס'],
  fxk_white_balance: ['Automatic white balance', 'איזון לבן אוטומטי'],
  fxk_exposure: ['Brighten the picture', 'הבהרת התמונה'],
  fxk_denoise_video: ['Clean up picture grain', 'ניקוי גרעיניות בתמונה'],
  fxk_stabilize: ['Stabilise', 'ייצוב'],
  fxk_fps: ['Constant frame rate', 'קצב פריימים קבוע'],
  fxk_upscale: ['Upscale', 'הגדלת רזולוציה'],
  fxk_highpass_hz: ['Rumble filter', 'מסנן המהום נמוך'],
  fxk_hum_notch_hz: ['Remove electrical hum', 'הסרת זמזום חשמל'],
  fxk_denoise_audio: ['Clean up background noise', 'ניקוי רעשי רקע'],
  fxk_declick: ['Remove clicks and pops', 'הסרת קליקים ופיצוצים'],
  fxk_loudness: ['Level the volume', 'השוואת עוצמת הקול'],
  fxMore: ['More fixes', 'תיקונים נוספים'],
  fxPreview: ['Preview 3 s', 'תצוגה מקדימה של 3 שנ׳'],
  fxPreviewAgain: ['Preview again', 'תצוגה מקדימה חדשה'],
  fxPreviewing: ['Rendering the preview…', 'מרנדר תצוגה מקדימה…'],
  fxPreviewFailed: ['The preview failed: {error}', 'התצוגה המקדימה נכשלה: {error}'],
  fxPreviewLater: ['Preview is available after {stage}.', 'התצוגה המקדימה תהיה זמינה אחרי שלב {stage}.'],
  fxBeforeAfter: ['Before and after', 'לפני ואחרי'],
  fxBefore: ['Before', 'לפני'],
  fxAfter: ['After', 'אחרי'],
  fxVoice: ['Voice clean-up', 'ניקוי קול'],
  fxUpscaler: ['Upscaler', 'הגדלת רזולוציה'],
  fxFree: ['free', 'חינם'],
  fxTierA: ['Tier A', 'רמה A'],
  fxTierB: ['Tier B · GPU', 'רמה B · GPU'],
  fxTierC: ['Tier C · paid', 'רמה C · בתשלום'],
  fxComing: ['Coming in Pilot {v}', 'יגיע בפיילוט {v}'],
  fxCost: ['≈ ${usd} for this source', '≈ {usd}$ למקור הזה'],
  fxProviderHint: ['Free options run inside the render. Paid ones show their price for this source.', 'האפשרויות החינמיות רצות בתוך הרינדור. ליד האפשרויות בתשלום מופיע המחיר למקור הזה.'],
  fxReset: ['Reset to recommended', 'חזרה להמלצה'],
  fxRerendering: ['Re-rendering {n} drafts', 'מרנדר מחדש {n} טיוטות'],
  fxRerenderingOne: ['Re-rendering 1 draft', 'מרנדר מחדש טיוטה אחת'],
  fxRerenderNone: ['Saved. No drafts use this source yet.', 'נשמר. עדיין אין טיוטות שמשתמשות במקור הזה.'],

  // ── Public review page ───────────────────────────────────────
  reviewTitle: ['Review for {customer}', 'לבדיקה של {customer}'],
  reviewWatermark: ['Review copy', 'עותק לבדיקה'],
  reviewExpires: ['This link expires {date}.', 'הקישור בתוקף עד {date}.'],
  reviewEmpty: ['Nothing to review yet. Your production team will send videos here when they are ready.', 'עוד אין מה לבדוק. צוות ההפקה ישלח לכאן סרטונים כשיהיו מוכנים.'],
  reviewGone: ['This review link has expired or was revoked', 'פג תוקף הקישור או שבוטל'],
  reviewGoneSub: ['Ask your production team for a new link.', 'בקשו קישור חדש מצוות ההפקה.'],
  reviewNotFound: ['Review link not found', 'הקישור לא נמצא'],
  poweredBy: ['Made with Castfeeds', 'נוצר עם Castfeeds'],
  // ── Devices: Castfeeds Engine on studio computers (local engine, plan §12) ─────
  nDevices: ['Devices', 'מחשבים'],
  teamTabMembers: ['Members', 'חברי צוות'],
  devicesSub: ['Studio computers running Castfeeds Engine. They process sessions on their own CPU and GPU; the original recordings never leave the studio.', 'מחשבי אולפן שמריצים את Castfeeds Engine. הם מעבדים הקלטות על המעבד והכרטיס הגרפי שלהם, וקובצי המקור לא יוצאים מהאולפן.'],
  addComputer: ['Add this computer', 'הוספת המחשב הזה'],
  pairTitle: ['Pair a studio computer', 'חיבור מחשב אולפן'],
  pairStep1: ['On the studio computer, open a terminal and run the line for that computer. It installs Castfeeds Engine, pairs it with the code below and starts it in the background.', 'במחשב האולפן פותחים חלון מסוף ומריצים את השורה שמתאימה לו. היא מתקינה את Castfeeds Engine, מחברת אותו עם הקוד שלמטה ומפעילה אותו ברקע.'],
  pairMac: ['Mac with Apple silicon: Terminal (no administrator password needed)', 'במחשב Mac עם מעבד Apple: פותחים את Terminal (לא צריך סיסמת מנהל)'],
  pairWindows: ['Windows 10/11: PowerShell, opened with “Run as administrator”', 'במחשב Windows 10/11: פותחים את PowerShell ב„הפעל כמנהל מערכת”'],
  pairStep2: ['The code works once and lasts 10 minutes. Engine already installed? Pair it with:', 'הקוד עובד פעם אחת ותקף ל-10 דקות. ה-Engine כבר מותקן? מחברים אותו כך:'],
  pairStep3: ['Then tell the engine where the recordings are:', 'אחר כך מגדירים ל-Engine איפה ההקלטות:'],
  pairAntivirus: ['Antivirus software may hold the engine back the first time it runs: see the user manual, “Install the engine”.', 'תוכנת אנטי-וירוס עלולה לעצור את ה-Engine בהפעלה הראשונה: ראו במדריך למשתמש, „התקנת ה-Engine”.'],
  pairCode: ['One-time code', 'קוד חד־פעמי'],
  pairExpiresIn: ['Expires in {time}', 'בתוקף עוד {time}'],
  pairExpired: ['This code expired. Create a new one.', 'פג תוקף הקוד. צרו קוד חדש.'],
  pairWaiting: ['Waiting for the computer…', 'מחכים למחשב…'],
  pairDone: ['Paired: {name}', 'המחשב חובר: {name}'],
  pairNewCode: ['New code', 'קוד חדש'],
  noDevices: ['No computers yet. Add the studio’s Mac or PC to process sessions locally.', 'עדיין אין מחשבים. הוסיפו את ה־Mac או ה־PC של האולפן כדי לעבד הקלטות במקום.'],
  deviceComputer: ['Computer', 'מחשב'],
  deviceStatus: ['Status', 'מצב'],
  deviceLastSeen: ['Last seen', 'נראה לאחרונה'],
  deviceHardware: ['Hardware', 'חומרה'],
  deviceWork: ['Work', 'עבודה'],
  devOnline: ['Online', 'מחובר'],
  devOffline: ['Offline', 'לא מחובר'],
  devRevoked: ['Revoked', 'בוטל'],
  deviceGpuProxies: ['{encoder} proxies', 'פרוקסי ב־{encoder}'],
  deviceCpuOnly: ['CPU only', 'מעבד בלבד'],
  deviceFreeDisk: ['{gb} GB free', '{gb} GB פנויים'],
  deviceJobs: ['{running} running · {queued} queued', '{running} בעבודה · {queued} בתור'],
  deviceFiles: ['{n} files', '{n} קבצים'],
  revokeDevice: ['Revoke', 'ביטול גישה'],
  confirmRevokeDevice: ['Revoke {name}? It stops at once. Sessions that use its files can then be processed in the cloud.', 'לבטל את הגישה של {name}? הוא יפסיק מייד. הקלטות שמשתמשות בקבצים שלו יוכלו לעבור לעיבוד בענן.'],
  renameDevice: ['Rename', 'שינוי שם'],
  routingTitle: ['Where sessions are processed', 'איפה מעבדים הקלטות'],
  routingSub: ['For sessions that use files on a studio computer. Uploaded sessions always process in the cloud.', 'להקלטות שמשתמשות בקבצים שעל מחשב באולפן. הקלטות שהועלו מעובדות תמיד בענן.'],
  locLocalFirst: ['Local first', 'מקומי קודם'],
  locLocalFirstSub: ['Use the studio computer; fall back to the cloud when it is offline.', 'משתמשים במחשב האולפן, ועוברים לענן כשהוא לא מחובר.'],
  locLocalOnly: ['Local only', 'מקומי בלבד'],
  locLocalOnlySub: ['Everything waits for the studio computer. Nothing is processed in the cloud.', 'הכול מחכה למחשב האולפן. שום דבר לא מעובד בענן.'],
  locCloudOnly: ['Cloud only', 'ענן בלבד'],
  locCloudOnlySub: ['Upload the files and process everything in the cloud.', 'מעלים את הקבצים ומעבדים הכול בענן.'],
  fastRenders: ['Fast renders (hardware encoder)', 'רינדור מהיר (מקודד חומרה)'],
  fastRendersSub: ['Final videos render on the GPU. Faster, but two renders of the same edit are not byte-identical.', 'הסרטונים הסופיים מרונדרים בכרטיס הגרפי. מהיר יותר, אבל שני רינדורים של אותה עריכה לא יהיו זהים ביט לביט.'],
  localTranscription: ['Transcribe on the studio computer', 'תמלול במחשב האולפן'],
  localTranscriptionSub: ['Computers that can do it well transcribe with the ivrit.ai Hebrew model on their own GPU. Off: transcription always runs in the cloud.', 'מחשבים שמסוגלים לכך מתמללים עם מודל העברית של ivrit.ai על הכרטיס הגרפי שלהם. כבוי: התמלול תמיד רץ בענן.'],
  engineUpdatesTitle: ['Engine updates', 'עדכוני Engine'],
  engineUpdatesSub: ['Computers update themselves between jobs, never in the middle of one.', 'המחשבים מתעדכנים בעצמם בין עבודות, אף פעם לא באמצע עבודה.'],
  engineChannel: ['Channel', 'ערוץ'],
  engineChannelStable: ['Stable', 'יציב'],
  engineChannelBeta: ['Beta (early versions)', 'בטא (גרסאות מוקדמות)'],
  enginePin: ['Pin a version', 'נעילת גרסה'],
  enginePinSub: ['Every computer installs exactly this version, also to roll back. Leave empty to follow the channel.', 'כל המחשבים מתקינים בדיוק את הגרסה הזאת, גם כדי לחזור לגרסה קודמת. השאירו ריק כדי לעקוב אחרי הערוץ.'],
  enginePinPlaceholder: ['e.g. 0.2.0', 'למשל 0.2.0'],
  enginePinInvalid: ['Use an exact version such as 0.2.0.', 'כתבו גרסה מדויקת, למשל 0.2.0.'],
  tierRecommended: ['Recommended', 'מומלץ'],
  tierMinimum: ['Minimum', 'מינימום'],
  tierBelowMinimum: ['Below minimum', 'מתחת למינימום'],
  tierUnknown: ['Not measured', 'לא נמדד'],
  tierHint: ['Hardware tier from the benchmark. It decides which steps this computer takes.', 'דרגת החומרה לפי מבחן הביצועים. היא קובעת אילו שלבים המחשב הזה מבצע.'],
  deviceSttLocal: ['Transcribes here · {backend}', 'מתמלל כאן · {backend}'],
  deviceSttNoModel: ['Model not downloaded · {backend}', 'המודל לא הורד · {backend}'],
  deviceSttCloud: ['Transcription in the cloud', 'תמלול בענן'],
  deviceUpdatesUnsigned: ['Updates not signed', 'עדכונים לא חתומים'],
  deviceUpdatesUnsignedHint: ['This engine has no Castfeeds release key yet: updates are checked by their SHA-256 only. Engines built after the key is added verify every update\'s signature.', 'ל־Engine הזה עוד אין מפתח שחרור של Castfeeds: עדכונים נבדקים רק לפי SHA-256. מנועים שייבנו אחרי הוספת המפתח יבדקו את החתימה של כל עדכון.'],
  deviceUpdateAvailable: ['Update {version} waiting for an idle moment', 'עדכון {version} מחכה לרגע פנוי'],
  localFilesTitle: ['Files on a studio computer', 'קבצים על מחשב באולפן'],
  localFilesSub: ['Use recordings in place: nothing is uploaded except proxies, audio and the finished videos.', 'משתמשים בהקלטות במקום שלהן: לא מועלה כלום חוץ מפרוקסי, אודיו והסרטונים המוכנים.'],
  localPickComputer: ['Computer', 'מחשב'],
  localNoFiles: ['No recordings registered on this computer yet. Run castfeeds-engine add-folder on it.', 'עדיין לא נרשמו הקלטות במחשב הזה. הריצו עליו castfeeds-engine add-folder.'],
  localNoComputer: ['No studio computer is paired. Ask an owner or admin to add one in Team → Devices.', 'עוד לא חובר מחשב אולפן. בקשו מבעלים או מנהל להוסיף אחד בצוות ← מחשבים.'],
  localFilter: ['Filter files', 'סינון קבצים'],
  localAdd: ['Add {n} files', 'הוספת {n} קבצים'],
  localAddOne: ['Add 1 file', 'הוספת קובץ אחד'],
  localAdded: ['On {name}', 'על {name}'],
  localTag: ['Local', 'מקומי'],
  localCloudOnly: ['This workspace processes in the cloud only. Upload the files instead.', 'סביבת העבודה הזו מעבדת בענן בלבד. העלו את הקבצים במקום.'],
  needsUploadTitle: ['{name} has been offline for a while', '{name} לא מחובר כבר זמן מה'],
  needsUploadBody: ['This session’s recordings are on that computer. Turn it on, or upload the recordings and process them in the cloud.', 'ההקלטות של הסשן הזה נמצאות על המחשב הזה. הדליקו אותו, או העלו את ההקלטות ועבדו אותן בענן.'],
  needsUploadRevoked: ['The computer with this session’s recordings was revoked. Upload the recordings to process them in the cloud.', 'הגישה של המחשב עם ההקלטות של הסשן בוטלה. העלו את ההקלטות כדי לעבד אותן בענן.'],
  processInCloud: ['Process in the cloud', 'עיבוד בענן'],
  cloudUploadTitle: ['Upload the recordings', 'העלאת ההקלטות'],
  cloudUploadSub: ['Pick these files from a copy of the recordings. Each one is matched by name and size.', 'בחרו את הקבצים האלה מעותק של ההקלטות. כל קובץ מותאם לפי שם וגודל.'],
  cloudUploadPick: ['Choose files', 'בחירת קבצים'],
  cloudUploadMatched: ['{n} of {total} matched', '{n} מתוך {total} הותאמו'],
  cloudUploadStart: ['Upload and continue', 'העלאה והמשך'],
  cloudUploadMissing: ['Not chosen yet', 'עוד לא נבחר'],
  processingOnDevice: ['Processing on {name}', 'מעובד על {name}'],
  processingWaitingDevice: ['Waiting for {name}', 'מחכים ל־{name}'],
  cap_devices: ['Manage studio computers', 'ניהול מחשבי אולפן'],

  // ── Camera rules and silence cuts (amendment 11) ─────────────
  kCamera: ['Camera rules', 'חוקי מצלמות'],
  kCameraSub: ['How the edit switches between cameras. Applies to new plans and regenerated videos.', 'איך העריכה עוברת בין המצלמות. חל על תכנון חדש ועל סרטונים שנוצרים מחדש.'],
  camDefaults: ['Pilot defaults', 'ברירות המחדל של הפיילוט'],
  camPreset: ['Preset', 'תבנית'],
  camPreset_calm_interview: ['Calm interview', 'ראיון רגוע'],
  camPreset_lively_panel: ['Lively panel', 'פאנל תוסס'],
  camPreset_social_fast: ['Social fast', 'קצב רשתות'],
  camPreset_custom: ['Custom', 'מותאם אישית'],
  camPresetTip_calm_interview: ['Longer shots, few cuts, no punch-ins in long videos.', 'שוטים ארוכים, מעט חיתוכים, בלי זום-אין בסרטונים ארוכים.'],
  camPresetTip_lively_panel: ['Follows the conversation closely, with regular wide shots.', 'עוקב מקרוב אחרי השיחה, עם שוטים רחבים לעיתים קרובות.'],
  camPresetTip_social_fast: ['Short shots and lots of variety for shorts and reels.', 'שוטים קצרים והרבה גיוון לסרטונים קצרים ולרילס.'],
  tier_full: ['Full episode', 'פרק מלא'],
  tier_value: ['Value videos', 'סרטוני ערך'],
  tier_social: ['Social videos', 'סרטונים לרשתות'],
  camMinShot: ['Shortest shot', 'שוט הכי קצר'],
  camMaxShot: ['Longest shot', 'שוט הכי ארוך'],
  camNoMax: ['No maximum', 'ללא הגבלה'],
  camMinTurn: ['Follow a new speaker after', 'לעבור לדובר חדש אחרי'],
  camWide: ['Wide shots', 'שוטים רחבים'],
  camWideTip: ['Share of speaker shots that cut to the wide or group camera. 0 = only when people talk over each other.', 'החלק מהשוטים של הדובר שעוברים למצלמה הרחבה או הקבוצתית. 0 = רק כשמדברים אחד על השני.'],
  camVariety: ['Visual variety', 'גיוון ויזואלי'],
  camVarietyTip: ['Higher rotates between more angles and varies where long shots are split.', 'ערך גבוה יותר מחליף בין יותר זוויות ומגוון את נקודות הפיצול של שוטים ארוכים.'],
  camReactions: ['Reaction shots on laughter', 'שוטי תגובה על צחוק'],
  camPunchIn: ['Punch in on long single-camera shots', 'זום-אין בשוט ארוך ממצלמה אחת'],
  camMaxBelowMin: ['The longest shot can’t be shorter than the shortest shot.', 'השוט הארוך לא יכול להיות קצר מהשוט הקצר.'],
  sec: ['{n} s', '{n} שנ׳'],
  // ── Reframe, layouts and music bed (amendment 21) ───────────
  camReframeHead: ['Framing and split screens', 'מסגור ומסך מפוצל'],
  camReframe: ['Follow faces', 'מעקב אחרי פנים'],
  camReframeSub: ['Keeps the speaker in frame in 9:16, 4:5 and 1:1 instead of a centre crop. Calm: small moves are ignored, big ones become a cut.', 'שומר את הדובר בפריים ב־9:16, 4:5 ו־1:1 במקום חיתוך מהמרכז. רגוע: תזוזות קטנות לא מזיזות את המסגרת, גדולות הופכות לחיתוך.'],
  camLayout: ['Fast exchanges', 'חילופי דברים מהירים'],
  camLayoutTip: ['What to show when two people go back and forth quickly.', 'מה להציג כששני אנשים מדברים לסירוגין במהירות.'],
  layout_single: ['One person', 'אדם אחד'],
  layout_auto: ['Auto', 'אוטומטי'],
  layout_split: ['Side by side', 'זה לצד זה'],
  layout_stack: ['Stacked', 'אחד מעל השני'],
  layout_pip: ['Picture in picture', 'תמונה בתוך תמונה'],
  layoutTip_single: ['Always cut to whoever is talking.', 'תמיד לעבור למי שמדבר.'],
  layoutTip_auto: ['Stacked for 9:16, 4:5 and 1:1; side by side for 16:9.', 'אחד מעל השני ב־9:16, 4:5 ו־1:1; זה לצד זה ב־16:9.'],
  layoutTip_split: ['Both people side by side.', 'שני האנשים זה לצד זה.'],
  layoutTip_stack: ['One person on top of the other.', 'אדם אחד מעל השני.'],
  layoutTip_pip: ['The first speaker full screen, the other in a corner.', 'הדובר הראשון במסך מלא, השני בפינה.'],
  camSplitWithin: ['Split when turns are shorter than', 'לפצל כשתורות קצרים מ־'],
  camSplitWithinTip: ['At least three turns in a row, each shorter than this, show both people at once.', 'לפחות שלושה תורות ברצף, כל אחד קצר מזה, מציגים את שני האנשים יחד.'],
  camReframeAllOff: ['Off for every tier', 'כבוי בכל השכבות'],
  camReframeAllOffTip: ['Turns face following off for full episodes, value and social videos at once. On a customer kit, every project that inherits it follows.', 'מכבה מעקב אחרי פנים בפרקים מלאים, בסרטוני ערך ובסרטונים לרשתות בבת אחת. בערכת לקוח, כל פרויקט שיורש ממנה מתעדכן.'],
  layPreviewSingle: ['One person at a time', 'אדם אחד בכל פעם'],
  layPreviewTwo: ['Fast exchange: {layout}', 'חילופי דברים מהירים: {layout}'],
  layPreviewReframe: ['faces centred', 'פנים במרכז'],
  kMusicBed: ['Music bed', 'מוזיקת רקע'],
  kMusicBedSub: ['A track under your videos that drops automatically while people talk.', 'רצועה מתחת לסרטונים שיורדת אוטומטית כשמדברים.'],
  musicUpload: ['Upload music', 'העלאת מוזיקה'],
  musicReplace: ['Replace music', 'החלפת מוזיקה'],
  musicNone: ['No track yet (MP3, M4A, WAV, FLAC or OGG, up to 30 MB, audio only).', 'עדיין אין רצועה (MP3,‏ M4A,‏ WAV,‏ FLAC או OGG, עד 30MB, שמע בלבד).'],
  musicPreview: ['Play the music bed', 'השמעת מוזיקת הרקע'],
  musicTooBig: ['Music files are limited to {mb} MB.', 'קובצי מוזיקה מוגבלים ל־{mb}MB.'],
  musicOn: ['Play music under videos', 'לנגן מוזיקה מתחת לסרטונים'],
  musicNeedsTrack: ['Upload a track first.', 'קודם מעלים רצועה.'],
  musicTiers: ['Use on', 'להשתמש ב־'],
  musicBed: ['Music level', 'עוצמת המוזיקה'],
  musicBedTip: ['How loud the music is between sentences, compared with the voices. −12 dB sits clearly behind them.', 'כמה חזקה המוזיקה בין המשפטים, ביחס לקולות. ‎−12 dB‎ נשאר ברור מאחוריהם.'],
  musicDuck: ['Lower under speech by', 'להנמיך בזמן דיבור ב־'],
  musicDuckTip: ['How much further the music drops while someone talks. It starts dropping just before the first word.', 'בכמה עוד יורדת המוזיקה כשמישהו מדבר. היא מתחילה לרדת רגע לפני המילה הראשונה.'],
  musicFadeIn: ['Fade in', 'כניסה הדרגתית'],
  musicFadeOut: ['Fade out', 'יציאה הדרגתית'],
  musicMode: ['Ducking', 'הנמכה'],
  musicMode_envelope: ['By words', 'לפי מילים'],
  musicMode_sidechain: ['By voice level', 'לפי עוצמת הקול'],
  musicModeTip_envelope: ['Uses the transcript: exact, never pumps between words.', 'לפי התמלול: מדויק, בלי קפיצות בין מילים.'],
  musicModeTip_sidechain: ['Follows the voice level like a compressor; for videos without a transcript.', 'עוקב אחרי עוצמת הקול כמו קומפרסור; לסרטונים בלי תמלול.'],
  musicRights: ['Use only music you have the rights to. That includes every platform you publish to.', 'השתמשו רק במוזיקה שיש לכם זכויות עליה, כולל בכל הפלטפורמות שבהן אתם מפרסמים.'],
  kSilence: ['Silence cuts', 'חיתוך שקט'],
  kSilenceSub: ['Cut silences by microphone level, like a jump-cut editor. Works with filler-word removal; a shot never loses more than half its length.', 'חיתוך קטעי שקט לפי עוצמת המיקרופונים, כמו עורך ג׳אמפ-קאט. עובד יחד עם הסרת מילות מילוי; שוט אף פעם לא מאבד יותר מחצי מהאורך שלו.'],
  silEnabled: ['Cut silences', 'לחתוך שקט'],
  silThreshold: ['Silence below', 'שקט מתחת ל־'],
  silThresholdTip: ['Every microphone must be quieter than this. −40 dB suits most studios; raise it (e.g. −35) for a noisy room.', 'כל המיקרופונים חייבים להיות שקטים מזה. ‎−40 dB‎ מתאים לרוב האולפנים; בחדר רועש העלו (למשל ‎−35‎).'],
  silMin: ['Shortest silence to cut', 'שקט מינימלי לחיתוך'],
  silPadding: ['Padding around speech', 'מרווח סביב הדיבור'],
  silKeep: ['Pause to keep', 'הפסקה שנשארת'],
  silOff: ['Off for this tier: only the transcript’s pauses are tightened.', 'כבוי בשכבה הזו: רק ההפסקות שבתמלול מתקצרות.'],
  ms: ['{n} ms', '{n} מ״ש'],
  db: ['{n} dB', '{n} דציבל'],
  // ── Group shots, all formats, editor timelines (amendment 11) ─
  filePeople: ['People in frame', 'מי בפריים'],
  filePeoplePh: ['e.g. Noa, Ido', 'למשל נועה, עידו'],
  filePeopleHint: ['For two- and three-shots: list everyone this camera sees.', 'לשוטים של שניים ושלושה: רשמו את כל מי שהמצלמה הזו רואה.'],
  allFormats: ['All formats', 'כל הפורמטים'],
  allFormatsTip: ['Make 9:16, 4:5, 1:1 and 16:9 versions with the same cut.', 'יצירת גרסאות 9:16, 4:5, 1:1 ו־16:9 עם אותו חיתוך.'],
  formatsCreated: ['{n} new formats are rendering.', '{n} פורמטים חדשים ברינדור.'],
  formatsExisting: ['Every format already exists.', 'כל הפורמטים כבר קיימים.'],
  variantBadge: ['Same cut', 'אותו חיתוך'],
  nleDownload: ['For Premiere / Resolve / Final Cut', 'ל־Premiere / Resolve / Final Cut'],
  nleHint: ['Timeline files (FCPXML, Premiere XML, EDL) and captions for the approved videos. They link to your original recordings: put the originals in the same folder (see README-media.txt).', 'קובצי טיימליין (FCPXML, Premiere XML, EDL) וכתוביות לסרטונים שאושרו. הם מקושרים להקלטות המקוריות: שימו את קובצי המקור באותה תיקייה (ראו README-media.txt).'],
  nlePreparing: ['Preparing timelines…', 'מכינים קובצי טיימליין…'],
  cap_branding: ['Domains, branding and e-mail sender', 'דומיינים, מיתוג וכתובת שולח'],

  // ── White-label (WS J · plan §14) ─────────────────────────────
  nSettings: ['Settings', 'הגדרות'],
  wlPoweredBy: ['Powered by Castfeeds', 'מופעל על ידי Castfeeds'],
  wlHelp: ['Help', 'עזרה'],
  wlTerms: ['Terms', 'תנאי שימוש'],
  wlPrivacy: ['Privacy', 'פרטיות'],
  wlInactiveTitle: ['This address isn’t active', 'הכתובת הזו לא פעילה'],
  wlInactiveBody: ['It isn’t connected yet, or it was turned off. Sign in at your usual address, or ask whoever sent you this link.', 'היא עוד לא חוברה, או שהושבתה. התחברו בכתובת הרגילה שלכם, או פנו למי ששלח לכם את הקישור.'],
  wlReviewOnlyTitle: ['This address shows review links', 'הכתובת הזו מציגה קישורי צפייה'],
  wlReviewOnlyBody: ['Open the review link you received by e-mail or message.', 'פתחו את קישור הצפייה שקיבלתם במייל או בהודעה.'],
  wlInviteOnly: ['Accounts on this address are by invitation. Ask your team admin for an invite.', 'חשבונות בכתובת הזו נפתחים בהזמנה בלבד. בקשו הזמנה ממנהל/ת הצוות.'],
  setTabDomains: ['Domains', 'דומיינים'],
  setTabDanger: ['Danger zone', 'אזור מסוכן'],
  setTabBranding: ['Branding', 'מיתוג'],
  setTabEmail: ['E-mail', 'מייל'],
  wlLevelNone: ['Your own domain and branding are included from the Pro plan. Contact Castfeeds to upgrade.', 'דומיין ומיתוג משלכם כלולים מחבילת Pro ומעלה. פנו ל-Castfeeds לשדרוג.'],
  wlLevelReview: ['Branded review links are included in your plan. Running the whole app on your domain is the white-label add-on.', 'קישורי צפייה ממותגים כלולים בחבילה שלכם. הרצת כל האפליקציה על הדומיין שלכם היא תוסף ה-White-label.'],
  wlLevelApp: ['White-label is on: the app, review links and e-mails can carry your domain and brand.', 'White-label פעיל: האפליקציה, קישורי הצפייה והמיילים יכולים לשאת את הדומיין והמותג שלכם.'],
  domAdd: ['Connect a domain', 'חיבור דומיין'],
  domName: ['Domain', 'דומיין'],
  domPurpose: ['Used for', 'משמש ל־'],
  domPurposeApp: ['The whole app', 'כל האפליקציה'],
  domPurposeReview: ['Review links', 'קישורי צפייה'],
  domLimit: ['{n} of {max} domains', '{n} מתוך {max} דומיינים'],
  domEmpty: ['No domains yet. Connect one to send review links from your own address.', 'עוד אין דומיינים. חברו דומיין כדי לשלוח קישורי צפייה מהכתובת שלכם.'],
  domStatus_pending: ['Waiting for DNS', 'ממתין ל-DNS'],
  domStatus_verified: ['Verified', 'מאומת'],
  domStatus_failed: ['Not verified', 'לא אומת'],
  domStatus_disabled: ['Disabled by support', 'הושבת על ידי התמיכה'],
  domCert_none: ['No certificate yet', 'עדיין אין תעודה'],
  domCert_requested: ['Certificate on first visit', 'תעודה בכניסה הראשונה'],
  domCert_issued: ['Secure (HTTPS)', 'מאובטח (HTTPS)'],
  domCert_error: ['Certificate problem', 'בעיה בתעודה'],
  domSteps: ['Add these two records at your DNS provider (Cloudflare, GoDaddy, Box, LiveDNS, …). Changes usually show within minutes; we keep checking for 7 days.', 'הוסיפו את שתי הרשומות האלה אצל ספק ה-DNS שלכם (Cloudflare, GoDaddy, Box, LiveDNS ועוד). השינוי נקלט בדרך כלל תוך דקות; אנחנו ממשיכים לבדוק במשך 7 ימים.'],
  domCloudflare: ['Cloudflare: set the CNAME to “DNS only” (grey cloud), or the certificate can’t be issued.', 'Cloudflare: הגדירו את ה-CNAME כ-“DNS only” (ענן אפור), אחרת לא ניתן להנפיק תעודה.'],
  domRecType: ['Type', 'סוג'],
  domRecName: ['Name', 'שם'],
  domRecValue: ['Value', 'ערך'],
  domRecOk: ['Found', 'נמצא'],
  domRecMissing: ['Not found yet', 'עוד לא נמצא'],
  domCheckNow: ['Check now', 'לבדוק עכשיו'],
  domChecking: ['Checking…', 'בודק…'],
  domLastChecked: ['Last checked {when}', 'נבדק לאחרונה {when}'],
  domOpen: ['Open', 'פתיחה'],
  domErr_verification_record_missing: ['The TXT record isn’t there yet.', 'רשומת ה-TXT עוד לא קיימת.'],
  domErr_verification_record_mismatch: ['A TXT record exists but its value is different. Copy the value again.', 'קיימת רשומת TXT אבל הערך שונה. העתיקו את הערך שוב.'],
  domErr_dns_target_missing: ['Verified. Now point the domain at us with the CNAME record.', 'אומת. עכשיו כוונו את הדומיין אלינו עם רשומת ה-CNAME.'],
  domErrGeneric: ['Last check: {error}', 'בדיקה אחרונה: {error}'],
  brProductName: ['Product name', 'שם המוצר'],
  brProductNameHint: ['Shown in the title, the sign-in page and e-mails.', 'מוצג בכותרת, בעמוד הכניסה ובמיילים.'],
  brLogo: ['Logo', 'לוגו'],
  brLogoDark: ['Logo for the dark theme', 'לוגו לערכת הנושא הכהה'],
  brFavicon: ['Favicon', 'אייקון לדפדפן'],
  brUpload: ['Upload', 'העלאה'],
  brUploading: ['Uploading…', 'מעלה…'],
  brAssetHint: ['PNG, SVG, JPG or WebP, up to 512 KB. Favicon: PNG, SVG or ICO, up to 128 KB.', 'PNG, SVG, JPG או WebP, עד 512KB. אייקון: PNG, SVG או ICO, עד 128KB.'],
  brColours: ['Colours', 'צבעים'],
  brAccent: ['Accent (light theme)', 'צבע מבטא (ערכה בהירה)'],
  brAccentDark: ['Accent (dark theme)', 'צבע מבטא (ערכה כהה)'],
  brSurfaceLight: ['Page background (light theme)', 'רקע העמוד (ערכה בהירה)'],
  brSurfaceDark: ['Page background (dark theme)', 'רקע העמוד (ערכה כהה)'],
  brDefaultColour: ['Default', 'ברירת מחדל'],
  brContrastTitle: ['Too hard to read (WCAG AA)', 'קשה מדי לקריאה (WCAG AA)'],
  brContrastIssue: ['{field} against {against}: {ratio}:1, needs {required}:1', '{field} מול {against}: ‎{ratio}:1, נדרש ‎{required}:1'],
  brUseSuggestion: ['Use {colour}', 'להשתמש ב-{colour}'],
  brLinks: ['Links', 'קישורים'],
  brHelpUrl: ['Help centre address', 'כתובת מרכז העזרה'],
  brTermsUrl: ['Terms of use address', 'כתובת תנאי השימוש'],
  brPrivacyUrl: ['Privacy policy address', 'כתובת מדיניות הפרטיות'],
  brSupportEmail: ['Support e-mail', 'מייל לתמיכה'],
  brDefaultLocale: ['Default language for new visitors', 'שפת ברירת מחדל למבקרים חדשים'],
  brVisitorChoice: ['Visitor’s browser', 'לפי הדפדפן של המבקר'],
  brPoweredBy: ['Show “Powered by Castfeeds”', 'להציג “מופעל על ידי Castfeeds”'],
  brPoweredByAddon: ['Removing it is an add-on. Contact Castfeeds.', 'הסרה היא תוסף בתשלום. פנו ל-Castfeeds.'],
  brPreview: ['Preview', 'תצוגה מקדימה'],
  brPreviewButton: ['Sign in', 'כניסה'],
  brPreviewLink: ['Forgot your password?', 'שכחת סיסמה?'],
  brSaved: ['Branding saved. Your domains show it within a minute.', 'המיתוג נשמר. הדומיינים שלכם יציגו אותו תוך דקה.'],
  emDisabled: ['Your own sender address is part of the white-label add-on. Meanwhile e-mails come from {from}, with your name.', 'כתובת שולח משלכם היא חלק מתוסף ה-White-label. בינתיים המיילים נשלחים מ-{from}, עם השם שלכם.'],
  emFromEmail: ['Sender address', 'כתובת השולח'],
  emFromName: ['Sender name', 'שם השולח'],
  emSetUp: ['Set up sender', 'הגדרת שולח'],
  emRecords: ['Add these records at your DNS provider so mail from {domain} reaches inboxes (SPF and DKIM).', 'הוסיפו את הרשומות האלה אצל ספק ה-DNS כדי שמיילים מ-{domain} יגיעו לתיבת הדואר (SPF ו-DKIM).'],
  emSpfMerge: ['If the domain already has an SPF record (v=spf1 …), add our include to it instead of creating a second one.', 'אם לדומיין כבר יש רשומת SPF‏ (v=spf1 …), הוסיפו אליה את ה-include שלנו במקום ליצור רשומה שנייה.'],
  emStatus_pending: ['Waiting for DNS', 'ממתין ל-DNS'],
  emStatus_verified: ['Verified: mail is sent from your address', 'מאומת: המיילים נשלחים מהכתובת שלכם'],
  emStatus_failed: ['Not verified', 'לא אומת'],
  emFallback: ['Until it is verified, e-mails come from {from} with your name.', 'עד האימות, המיילים נשלחים מ-{from} עם השם שלכם.'],
  emSendTest: ['Send a test e-mail', 'שליחת מייל בדיקה'],
  emTestSent: ['Sent to {to} from {from}', 'נשלח אל {to} מ-{from}'],
  emRemove: ['Remove sender', 'הסרת השולח'],
  emErr_dkim_record_missing: ['The DKIM record isn’t there yet.', 'רשומת ה-DKIM עוד לא קיימת.'],
  emErr_spf_record_missing: ['The SPF record doesn’t include our servers yet.', 'רשומת ה-SPF עוד לא כוללת את השרתים שלנו.'],
  emErr_postmark_account_token_missing: ['Your Castfeeds server can’t register sender domains automatically yet. Contact support.', 'שרת ה-Castfeeds שלכם עוד לא יכול לרשום דומיינים לשליחה אוטומטית. פנו לתמיכה.'],
  emPurpose_spf: ['SPF', 'SPF (שליחה)'],
  emPurpose_dkim: ['DKIM', 'DKIM (חתימה)'],
  emPurpose_dmarc: ['DMARC (recommended)', 'DMARC (מומלץ)'],
  emPurpose_return_path: ['Return path', 'כתובת החזרה'],

  // ── Billing (WS G · plan §11.1) ─────────────────────────────
  nBilling: ['Billing', 'חיוב'],
  billTitle: ['Billing', 'חיוב'],
  billSub: ['Your plan, payment details, invoices and AI credits.', 'התוכנית, פרטי התשלום, החשבוניות וקרדיטי ה-AI שלכם.'],
  billOwnerOnly: ['Only the workspace owner manages billing.', 'רק בעלי סביבת העבודה מנהלים את החיוב.'],
  billDetails: ['Billing details', 'פרטי חיוב'],
  billDetailsSub: ['Invoices are addressed to these details. Country and currency decide how you pay.', 'החשבוניות מופקות לפרטים האלה. המדינה והמטבע קובעים איך משלמים.'],
  billLegalName: ['Legal name', 'שם משפטי'],
  billEmail: ['Billing e-mail', 'אימייל לחשבוניות'],
  billCountry: ['Country', 'מדינה'],
  billCurrency: ['Currency', 'מטבע'],
  billCurrencyLocked: ['The currency is fixed while you have a paid subscription.', 'המטבע קבוע כל עוד יש מנוי בתשלום.'],
  billVatId: ['Company or VAT number', 'ח.פ. / ע.מ.'],
  billVatHint: ['Israeli businesses: needed on the tax invoice (and for the allocation number).', 'לעסקים בישראל: נדרש בחשבונית המס (ולמספר ההקצאה).'],
  billAddress: ['Address', 'כתובת'],
  billCity: ['City', 'עיר'],
  billPostal: ['Postal code', 'מיקוד'],
  billSaveDetails: ['Save details', 'שמירת הפרטים'],
  billDetailsFirst: ['Fill in your billing details first.', 'מלאו קודם את פרטי החיוב.'],
  billCurrentPlan: ['Your plan', 'התוכנית שלך'],
  billNoPlan: ['No plan yet. Choose one below: new workspaces start with a free trial.', 'עדיין אין תוכנית. בחרו אחת למטה: סביבות עבודה חדשות מתחילות בתקופת ניסיון חינם.'],
  billStatus_trialing: ['Free trial', 'תקופת ניסיון'],
  billStatus_active: ['Active', 'פעילה'],
  billStatus_past_due: ['Payment overdue', 'התשלום באיחור'],
  billStatus_cancelled: ['Cancelled', 'בוטלה'],
  billPerMonth: ['{price} / month', '{price} לחודש'],
  billPerYear: ['{price} / year', '{price} לשנה'],
  billTrialEnds: ['Trial ends {date}. Your card is charged then.', 'הניסיון מסתיים ב-{date}. הכרטיס יחויב אז.'],
  billRenews: ['Renews {date}.', 'מתחדשת ב-{date}.'],
  billEndsOn: ['Ends {date}. You keep full access until then.', 'מסתיימת ב-{date}. עד אז יש לכם גישה מלאה.'],
  billResume: ['Keep my subscription', 'השארת המנוי'],
  billCancel: ['Cancel subscription', 'ביטול המנוי'],
  billCancelConfirm: ['Cancel at the end of the period? You keep everything until {date}.', 'לבטל בסוף התקופה? הכול נשאר זמין עד {date}.'],
  billCancelYes: ['Yes, cancel at period end', 'כן, לבטל בסוף התקופה'],
  billKeep: ['No, keep it', 'לא, להשאיר'],
  billPendingDowngrade: ['Switches to {plan} on {date}.', 'עוברת ל-{plan} ב-{date}.'],
  billPastDue: ['We couldn’t charge your card. Update it to keep your workspace active.', 'לא הצלחנו לחייב את הכרטיס. עדכנו אותו כדי שסביבת העבודה תישאר פעילה.'],
  billRetrying: ['The last charge failed; we retry automatically (attempt {n}). Updating the card retries at once.', 'החיוב האחרון נכשל; ננסה שוב אוטומטית (ניסיון {n}). עדכון הכרטיס מנסה מיד.'],
  roSuspended: ['This workspace is suspended and is read-only. Contact Castfeeds support.', 'סביבת העבודה מושעית ובמצב קריאה בלבד. פנו לתמיכה של Castfeeds.'],
  roPendingDeletion: ['This workspace is scheduled for deletion and is read-only. An owner can cancel it in Settings → Danger zone, or Castfeeds support can restore it.', 'סביבת העבודה מתוזמנת למחיקה ובמצב קריאה בלבד. בעלים יכולים לבטל בהגדרות ← אזור מסוכן, או שהתמיכה של Castfeeds יכולה לשחזר אותה.'],
  roBillingOwner: ['This workspace is read-only because of an unpaid invoice. Pay on the Billing page: access returns as soon as the payment goes through.', 'סביבת העבודה במצב קריאה בלבד בגלל חשבונית שלא שולמה. שלמו בעמוד החיוב: הגישה תחזור ברגע שהתשלום יעבור.'],
  roBillingMember: ['This workspace is read-only because of an unpaid invoice. Ask a workspace owner to pay on the Billing page.', 'סביבת העבודה במצב קריאה בלבד בגלל חשבונית שלא שולמה. בקשו מבעלי סביבת העבודה לשלם בעמוד החיוב.'],
  roPayNow: ['Go to Billing', 'למעבר לחיוב'],
  billSuspended: ['Your workspace is read-only because of an unpaid invoice. Update your card: access returns as soon as the payment goes through.', 'סביבת העבודה במצב קריאה בלבד בגלל חשבונית שלא שולמה. עדכנו את הכרטיס: הגישה תחזור ברגע שהתשלום יעבור.'],
  billPlans: ['Plans', 'תוכניות'],
  billIncluded: ['{credits} AI credits and {storage} a month', '{credits} קרדיטי AI ו-{storage} בחודש'],
  billTrialDays: ['{days}-day free trial', '{days} ימי ניסיון חינם'],
  billNotSold: ['Not sold in {currency}', 'לא נמכרת ב-{currency}'],
  billCurrent: ['Current plan', 'התוכנית הנוכחית'],
  billChoose: ['Choose', 'בחירה'],
  billStartTrial: ['Start free trial', 'התחלת ניסיון חינם'],
  billUpgrade: ['Upgrade', 'שדרוג'],
  billDowngrade: ['Downgrade', 'שנמוך'],
  billUpgraded: ['Done. You were charged {amount} for the rest of this period.', 'בוצע. חויבתם ב-{amount} על יתרת התקופה.'],
  billChanged: ['Your plan was changed.', 'התוכנית שונתה.'],
  billScheduled: ['Your plan changes at the end of this period.', 'התוכנית תשתנה בסוף התקופה הנוכחית.'],
  billPaymentMethod: ['Payment method', 'אמצעי תשלום'],
  billNoCard: ['No saved card.', 'אין כרטיס שמור.'],
  billCardExp: ['expires {date}', 'בתוקף עד {date}'],
  billUpdateCard: ['Update card', 'עדכון כרטיס'],
  billManagedBy: ['Your card is kept by {provider}.', 'הכרטיס שמור אצל {provider}.'],
  billProcessedBy: ['Payments are processed by {provider} on its secure page. Castfeeds never sees your card number.', 'התשלומים מעובדים ב-{provider} בדף המאובטח שלו. Castfeeds לא רואה את מספר הכרטיס.'],
  billMerchantOfRecord: ['{provider} is the seller of record: your invoice and VAT come from {provider}.', '{provider} הוא המוכר הרשום: החשבונית והמע״מ מגיעים מ-{provider}.'],
  billManualOnly: ['Online payment isn’t available for your country and currency yet. Castfeeds will send you an invoice.', 'תשלום מקוון עדיין לא זמין למדינה ולמטבע שלכם. Castfeeds תשלח לכם חשבונית.'],
  billCredits: ['AI credits', 'קרדיטי AI'],
  billBalance: ['Balance: {credits} credits', 'יתרה: {credits} קרדיטים'],
  billCreditsHint: ['1 credit = $0.01. Pack credits last 12 months; every paid job shows its cost first.', 'קרדיט אחד = 0.01$. קרדיטים מחבילה תקפים 12 חודשים; כל עבודה בתשלום מציגה את העלות מראש.'],
  billBuy: ['Buy', 'קנייה'],
  billInvoices: ['Invoices', 'חשבוניות'],
  billNoInvoices: ['No invoices yet.', 'עדיין אין חשבוניות.'],
  billNumber: ['Number', 'מספר'],
  billDate: ['Date', 'תאריך'],
  billAmount: ['Amount', 'סכום'],
  billInvStatus_open: ['Open', 'פתוחה'],
  billInvStatus_paid: ['Paid', 'שולמה'],
  billInvStatus_void: ['Void', 'מבוטלת'],
  billInvStatus_refunded: ['Refunded', 'הוחזרה'],
  billRefunded: ['{amount} refunded', 'הוחזרו {amount}'],
  billDownload: ['Download', 'הורדה'],
  billDocPending: ['Tax document on its way', 'מסמך המס בדרך'],
  billRedirecting: ['Opening the secure payment page…', 'פותחים את דף התשלום המאובטח…'],
  billReturnPending: ['We’re confirming your payment with the provider…', 'מאשרים את התשלום מול הספק…'],
  billReturnOk: ['Payment received. Thank you!', 'התשלום התקבל. תודה!'],
  billReturnFailed: ['The payment didn’t go through: {reason}', 'התשלום לא עבר: {reason}'],
  billReturnCancelled: ['Checkout cancelled. Nothing was charged.', 'התשלום בוטל. לא חויבתם.'],
  billReturnSlow: ['The confirmation is taking longer than usual. Refresh in a minute; you won’t be charged twice.', 'האישור לוקח יותר זמן מהרגיל. רעננו בעוד דקה; לא תחויבו פעמיים.'],
  payOpening: ['Opening the secure checkout…', 'פותחים את דף התשלום המאובטח…'],
  payUnavailable: ['This payment link isn’t available. Go back to Billing and start again.', 'קישור התשלום הזה לא זמין. חזרו לעמוד החיוב והתחילו מחדש.'],

  // ── Approvals and collaboration (development plan §17; prototype v_09approvals) ──
  apFilterAll: ['All', 'הכול'],
  apFilterWaiting: ['Waiting on me', 'מחכה לי'],
  apFilterCustomer: ['With customer', 'אצל הלקוח'],
  apFilterApproved: ['Approved', 'אושר'],
  apStep_edit: ['Edit', 'עריכה'],
  apStep_internal: ['Internal', 'פנימי'],
  apStep_customer: ['Customer', 'לקוח'],
  apStep_publish: ['Publish', 'פרסום'],
  apEmpty: ['Nothing is waiting for approval. Send videos here from an episode’s drill-down.', 'אין כרגע סרטונים לאישור. שולחים לכאן סרטונים מעמוד הפירוק של פרק.'],
  apEmptyFilter: ['Nothing here right now.', 'אין כאן כלום כרגע.'],
  apPick: ['Pick a video on the left to review it.', 'בחרו סרטון מימין כדי לבדוק אותו.'],
  apApproved: ['Approved ✓', 'אושר ✓'],
  apApproveAsCustomer: ['Approve as customer', 'אישור בשם הלקוח'],
  apApproveAsCustomerConfirm: ['Record {customer}’s approval for them? This is logged with your name.', 'לרשום את האישור של {customer} בשמם? הפעולה נרשמת בשמך.'],
  reqChanges: ['Request changes', 'בקשת שינויים'],
  reqChangesNote: ['What should change?', 'מה צריך לשנות?'],
  apNeeds: ['{n} of {required} customer approvals', '{n} מתוך {required} אישורי לקוח'],
  apOnBehalf: ['for the customer', 'בשם הלקוח'],
  apAuto: ['Auto-approved (no reply)', 'אושר אוטומטית (ללא תגובה)'],
  apScheduled: ['Scheduled', 'מתוזמן'],
  apWaiting: ['Waiting', 'ממתין'],
  apNoVideo: ['The video for this version is still rendering.', 'הסרטון של הגרסה הזו עדיין ברינדור.'],
  versionDiff: ['What changed', 'מה השתנה'],
  versionDiffNone: ['No notes for this version.', 'אין הערות לגרסה הזו.'],
  shareTitle: ['Customer review link', 'קישור לבדיקת הלקוח'],
  shareSub: ['Watermarked, no download, comments only.', 'עם סימן מים, בלי הורדה, הערות בלבד.'],
  shareCreate: ['Create review link', 'יצירת קישור לבדיקה'],
  comments: ['Comments', 'הערות'],
  commentsOpen: ['{n} open', '{n} פתוחות'],
  commentAt: ['Comment at {tc}…', 'הערה ב-{tc}…'],
  commentInternal: ['Team only', 'לצוות בלבד'],
  commentInternalTip: ['The customer never sees team-only comments.', 'הלקוח לא רואה הערות לצוות בלבד.'],
  commentNone: ['No comments yet.', 'עדיין אין הערות.'],
  commentResolve: ['Resolve', 'סימון כטופל'],
  commentReopen: ['Reopen', 'פתיחה מחדש'],
  commentVersion: ['on v{v}', 'על v{v}'],
  post: ['Post', 'שליחה'],
  roleCustomer: ['Customer', 'לקוח'],
  sendToApproval: ['Send {n} to approval', 'שליחת {n} לאישור'],
  sendToApprovalHint: ['Approved videos go to the Approvals screen: internal review, then the customer.', 'סרטונים שאושרו עוברים למסך האישורים: בדיקה פנימית ואז הלקוח.'],
  sentToApproval: ['Sent {n} to approval', '{n} נשלחו לאישור'],
  reviewYourName: ['Your name', 'השם שלך'],
  reviewYourNameHint: ['Shown with your comments and decisions.', 'יוצג ליד ההערות וההחלטות שלך.'],
  reviewApproved: ['You approved this version.', 'אישרת את הגרסה הזו.'],
  reviewWithStudio: ['Back with the production team for changes.', 'חזר לצוות ההפקה לשינויים.'],
  reviewThanks: ['Thanks, your decision was sent.', 'תודה, ההחלטה נשלחה.'],
  // ── Publishing status on posts (WS O · amendment 20) ─────────
  st_publishing: ['Publishing', 'בפרסום'],
  postViewLive: ['View post', 'לפוסט'],
  postRetry: ['Retry', 'ניסיון חוזר'],
  postRetried: ['Back on the schedule; it goes out within a minute.', 'הפוסט חזר ללוח ויצא בתוך דקה.'],
  postAutoReady: ['Posts by itself', 'יפורסם אוטומטית'],
  postAutoManual: ['Post by hand', 'פרסום ידני'],
  postAutoNotConnected: ['Not connected', 'לא מחובר'],
  postAutoOff: ['Coming soon', 'בקרוב'],
  postAttempt: ['Attempt {n}; next try {when}', 'ניסיון {n}; הניסיון הבא {when}'],
  // ── Platform hints on posts (amendment 25 part 1: G-M4, G-T7) ─────────
  perr_page_authorization: ['Facebook asks for Page Publishing Authorization on this Page. Complete it in Facebook, then choose Retry.', 'Facebook דורש לעמוד הזה אישור פרסום (Page Publishing Authorization). יש להשלים אותו ב-Facebook ואז לבחור ״ניסיון חוזר״.'],
  postHintIgStories: ['Instagram Stories can be posted through the API from a business account only, not a creator account.', 'אפשר לפרסם Stories ב-Instagram דרך הממשק רק מחשבון עסקי, לא מחשבון יוצר.'],
  postHintTikTokProcessing: ['TikTok is processing the video. It can take a few minutes to appear on your profile.', 'TikTok מעבד את הסרטון. ייתכן שיעברו כמה דקות עד שיופיע בפרופיל שלכם.'],
  // ── The Compose step (amendment 25 part 2): the project setting; the Publish screen's and the sheet's strings are in compose.ts ─────────
  ytAudienceSetting: ['Audience on YouTube', 'קהל ב-YouTube'],
  ytAudienceSettingHint: ['Required before this project posts to YouTube by itself. Each post can change it in Compose.', 'חובה לפני שהפרויקט מפרסם ב-YouTube אוטומטית. אפשר לשנות לכל פוסט בחלון העריכה.'],
  ytAudienceNotSet: ['Not set', 'לא הוגדר'],
  ytAudience_made_for_kids: ['Made for kids', 'מיועד לילדים'],
  ytAudience_not_made_for_kids: ['Not made for kids', 'לא מיועד לילדים'],
  // ── Data deletion instructions, a public page for Meta's app settings (amendment 25 part 1: G-M1 interim) ─────────
  ddTitle: ['Delete your Facebook and Instagram data', 'מחיקת המידע שלכם מ-Facebook ומ-Instagram'],
  ddSub: ['Castfeeds stores only what it needs to publish the videos you schedule: your Facebook user ID, name and picture; the names, IDs and pictures of the Facebook Pages and Instagram professional accounts you chose; encrypted access tokens; and the IDs and links of the posts we published for you.', 'Castfeeds שומרת רק את מה שנדרש כדי לפרסם את הסרטונים שתזמנתם: מזהה המשתמש, השם והתמונה שלכם ב-Facebook; השמות, המזהים והתמונות של עמודי ה-Facebook וחשבונות ה-Instagram המקצועיים שבחרתם; טוקני גישה מוצפנים; והמזהים והקישורים של הפוסטים שפרסמנו עבורכם.'],
  ddHow: ['To delete it, do any one of these:', 'כדי למחוק את המידע, בצעו אחת מהפעולות הבאות:'],
  ddInApp: ['In Castfeeds: open Integrations, find Instagram or Facebook and choose Disconnect. We revoke our access at Meta and delete the connection immediately.', 'ב-Castfeeds: פתחו את עמוד החיבורים, מצאו את Instagram או Facebook ובחרו ״ניתוק״. אנחנו מבטלים את הגישה שלנו אצל Meta ומוחקים את החיבור מיד.'],
  ddInFacebook: ['In Facebook: open Settings & privacy → Settings → Business integrations (or Apps and websites), select Castfeeds and choose Remove. Then email us as below so we delete the stored details too.', 'ב-Facebook: פתחו הגדרות ופרטיות ← הגדרות ← שילובים עסקיים (או אפליקציות ואתרים), בחרו Castfeeds ולחצו ״הסרה״. אחר כך כתבו לנו כמו בסעיף הבא כדי שנמחק גם את הפרטים השמורים.'],
  ddByEmail: ['By email: write to {email} from your account’s email address with the subject “Delete my Meta data”. We confirm within 30 days, usually within 2 business days.', 'בדוא״ל: כתבו ל-{email} מהכתובת שרשומה בחשבון, עם הנושא ״מחיקת המידע שלי מ-Meta״. נאשר תוך 30 יום, בדרך כלל תוך 2 ימי עסקים.'],
  ddAfter: ['Posts already published stay on Facebook and Instagram; delete them there if you wish. To delete your whole Castfeeds account, see our privacy policy.', 'פוסטים שכבר פורסמו נשארים ב-Facebook וב-Instagram; אפשר למחוק אותם שם. למחיקת כל חשבון ה-Castfeeds, ראו את מדיניות הפרטיות שלנו.'],
  perr_not_connected: ['Connect this channel in Integrations, or post it by hand.', 'יש לחבר את הערוץ בעמוד החיבורים, או לפרסם ידנית.'],
  perr_no_render: ['The video has no finished render of its current version yet.', 'לגרסה הנוכחית של הסרטון עדיין אין רינדור מוכן.'],
  perr_rate_limited: ['The platform’s posting limit was reached; it goes out when the limit resets.', 'הגעתם למגבלת הפרסום של הפלטפורמה; הפוסט ייצא כשהמגבלה תתאפס.'],
  perr_needs_check: ['The platform did not confirm the post. Check the account before retrying, so it is not posted twice.', 'הפלטפורמה לא אישרה את הפוסט. כדאי לבדוק בחשבון לפני ניסיון חוזר, כדי שלא יפורסם פעמיים.'],
  perr_token_expired: ['The connection expired. Reconnect it in Integrations, then retry.', 'פג תוקף החיבור. יש לחבר מחדש בעמוד החיבורים ואז לנסות שוב.'],
  perr_target_missing: ['Choose the channel, page or account to post to in Integrations.', 'יש לבחור בעמוד החיבורים לאיזה ערוץ, עמוד או חשבון לפרסם.'],
  perr_platform_rejected: ['The platform refused the post (format, length or policy).', 'הפלטפורמה דחתה את הפוסט (פורמט, אורך או מדיניות).'],
  perr_upload_failed: ['The upload did not finish; it is retried automatically.', 'ההעלאה לא הסתיימה; ננסה שוב אוטומטית.'],
  perr_workspace_suspended: ['Held while the workspace was suspended. When it is overdue by more than an hour, choose Retry to post it now.', 'הפוסט הושהה בזמן שסביבת העבודה הייתה מושעית. אם הוא באיחור של יותר משעה, יש לבחור ״ניסיון חוזר״ כדי לפרסם אותו עכשיו.'],
  // ── Integrations (prototype v_13integ; WS O) ─────────────────
  integSubWeb: ['Connect the channels Castfeeds posts to. Tokens are encrypted, refreshed automatically and never shown.', 'מחברים את הערוצים ש‑Castfeeds מפרסם בהם. הטוקנים מוצפנים, מתחדשים אוטומטית ולעולם לא מוצגים.'],
  integSocial: ['Social channels', 'ערוצים חברתיים'],
  integSocialSub: ['Publishing straight from the Publish screen', 'פרסום ישיר ממסך הפרסום'],
  integManual: ['Post by hand', 'פרסום ידני'],
  integManualSub: ['No posting API yet: use the download pack', 'עדיין אין ממשק פרסום: משתמשים בחבילת ההורדה'],
  integConnect: ['Connect', 'חיבור'],
  integReconnect: ['Reconnect', 'חיבור מחדש'],
  integDisconnect: ['Disconnect', 'ניתוק'],
  integDisconnectConfirm: ['Disconnect {platform}? Scheduled posts on it wait until you connect again.', 'לנתק את {platform}? פוסטים מתוזמנים בו ימתינו עד שתתחברו שוב.'],
  integCheck: ['Check', 'בדיקה'],
  integPostTo: ['Post to', 'פרסום אל'],
  integScope: ['Use for', 'שימוש עבור'],
  integAllProjects: ['All projects', 'כל הפרויקטים'],
  integProjectOnly: ['Only {project}', 'רק {project}'],
  integExpires: ['Renew by {date}', 'לחדש עד {date}'],
  integLastCheck: ['Checked {when}', 'נבדק {when}'],
  integModeSandbox: ['Sandbox', 'סביבת ניסוי'],
  integModeSandboxTip: ['Test mode: nothing is posted; the whole flow runs with a fake account.', 'מצב ניסוי: שום דבר לא מתפרסם; כל התהליך רץ עם חשבון מדומה.'],
  integModeOff: ['Coming soon', 'בקרוב'],
  integModeOffTip: ['Waiting for the platform’s app review.', 'ממתין לאישור האפליקציה אצל הפלטפורמה.'],
  integNeeds_youtube: ['A Google account with a YouTube channel.', 'חשבון Google עם ערוץ YouTube.'],
  integNeeds_instagram: ['An Instagram professional account (Business or Creator) linked to a Facebook Page you manage.', 'חשבון Instagram מקצועי (עסקי או יוצר) שמקושר לעמוד Facebook שאתם מנהלים.'],
  integNeeds_facebook: ['A Facebook Page you manage.', 'עמוד Facebook שאתם מנהלים.'],
  integNeeds_tiktok: ['A TikTok account. Until our app passes TikTok’s audit, posts are private.', 'חשבון TikTok. עד שהאפליקציה שלנו תעבור את הביקורת של TikTok, הפוסטים פרטיים.'],
  integNeeds_linkedin: ['A LinkedIn profile (company pages need admin rights).', 'פרופיל LinkedIn (עמודי חברה דורשים הרשאות מנהל).'],
  // LinkedIn's two apps (amendment 25 part 3, G-L1)
  integLiProfile: ['Your profile', 'הפרופיל שלך'],
  integLiPage: ['Company page', 'עמוד חברה'],
  integLiConnectAria: ['Connect LinkedIn: {what}', 'חיבור LinkedIn: {what}'],
  integLiSwitchTo: ['Switch to {what}', 'מעבר ל{what}'],
  integLiReplace: ['This replaces the connection to {account} ({current}) with {what}. Posts scheduled to it will need a new confirmation: they go back to draft until you schedule them again.', 'הפעולה מחליפה את החיבור ל-{account} ({current}) ב{what}. פוסטים שתוזמנו אליו יצטרכו אישור חדש: הם יחזרו לטיוטה עד שתתזמנו אותם שוב.'],
  integConnected: ['{platform} is connected.', '{platform} חובר.'],
  integNoConnections: ['Not connected', 'לא מחובר'],
  health_connected: ['Connected', 'מחובר'],
  health_expiring: ['Renew soon', 'לחדש בקרוב'],
  health_expired: ['Expired', 'פג תוקף'],
  health_revoked: ['Revoked', 'בוטל'],
  health_error: ['Not responding', 'לא מגיב'],
  ierr_access_denied: ['You declined the permissions, so nothing was connected.', 'לא אישרתם את ההרשאות, ולכן שום דבר לא חובר.'],
  ierr_invalid_state: ['That sign-in link was already used or is not ours. Start again with Connect.', 'קישור ההתחברות כבר נוצל או שאינו שלנו. התחילו שוב עם "חיבור".'],
  ierr_expired_state: ['The sign-in took longer than 10 minutes. Start again with Connect.', 'ההתחברות ארכה יותר מ‑10 דקות. התחילו שוב עם "חיבור".'],
  ierr_missing_state: ['The platform sent us back without the sign-in details. Start again with Connect.', 'הפלטפורמה החזירה אותנו בלי פרטי ההתחברות. התחילו שוב עם "חיבור".'],
  ierr_target_missing: ['Connected, but the account has nothing to post to (see what this platform needs).', 'החשבון חובר, אבל אין בו יעד לפרסום (ראו מה הפלטפורמה דורשת).'],
  ierr_platform_off: ['This platform is not available yet.', 'הפלטפורמה הזאת עדיין לא זמינה.'],
  ierr_consent_failed: ['The platform did not complete the sign-in. Try again.', 'הפלטפורמה לא השלימה את ההתחברות. נסו שוב.'],
  ierr_connect_failed: ['Connecting failed on the platform’s side. Try again in a few minutes.', 'החיבור נכשל בצד הפלטפורמה. נסו שוב בעוד כמה דקות.'],
  inviteCustomer: ['Reviews for customer', 'בודק עבור הלקוח'],
  // ── Tasks, activity feed and presence (development plan §19.4, amendment 19 part 1) ──
  nTasks: ['Tasks', 'משימות'],
  tkSub: ['The team’s work, from the pipeline, the customer and each other.', 'העבודה של הצוות: מהעיבוד, מהלקוח ואחד מהשני.'],
  tkCol_todo: ['To do', 'לביצוע'],
  tkCol_in_progress: ['In progress', 'בעבודה'],
  tkCol_needs_you: ['Needs you', 'ממתין לך'],
  tkCol_done: ['Done', 'בוצע'],
  tkStatus_todo: ['To do', 'לביצוע'],
  tkStatus_in_progress: ['In progress', 'בעבודה'],
  tkStatus_needs_you: ['Needs you', 'ממתין לך'],
  tkStatus_done: ['Done', 'בוצע'],
  tkStatus_cancelled: ['Cancelled', 'בוטל'],
  tkStatus_failed: ['Failed', 'נכשל'],
  tkType_ingest_check: ['Recording check', 'בדיקת הקלטה'],
  tkType_edit_apply_changes: ['Apply changes', 'ביצוע שינויים'],
  tkType_review_precheck: ['Review check', 'בדיקה לפני אישור'],
  tkType_publish_plan: ['Publishing', 'פרסום'],
  tkType_customer_chase: ['Chase the customer', 'מעקב מול הלקוח'],
  tkType_customer_reply: ['Reply to the customer', 'מענה ללקוח'],
  tkType_ops_diagnose: ['Something failed', 'משהו נכשל'],
  tkType_custom: ['Task', 'משימה'],
  tkPrio_low: ['Low', 'נמוכה'],
  tkPrio_normal: ['Normal', 'רגילה'],
  tkPrio_high: ['High', 'גבוהה'],
  tkPrio_urgent: ['Urgent', 'דחוף'],
  tkFilterProject: ['Project', 'פרויקט'],
  tkFilterAssignee: ['Assignee', 'אחראי/ת'],
  tkFilterType: ['Type', 'סוג'],
  tkAllProjects: ['All projects', 'כל הפרויקטים'],
  tkAllTypes: ['All types', 'כל הסוגים'],
  tkAnyone: ['Anyone', 'כולם'],
  tkMe: ['Me', 'אני'],
  tkUnassigned: ['Unassigned', 'לא שויך'],
  tkEmptyCol: ['Nothing here.', 'אין כאן כלום.'],
  tkEmpty: ['No tasks yet. They appear when a session needs a hand, a customer comments or asks for changes, or someone creates one.', 'עדיין אין משימות. הן נפתחות כשהקלטה צריכה טיפול, כשלקוח מגיב או מבקש שינויים, או כשמישהו יוצר משימה.'],
  tkCreate: ['Create task…', 'יצירת משימה…'],
  tkCreateTitle: ['New task', 'משימה חדשה'],
  tkTitle: ['Title', 'כותרת'],
  tkBrief: ['What to do', 'מה צריך לעשות'],
  tkAssignee: ['Assignee', 'אחראי/ת'],
  tkPriority: ['Priority', 'עדיפות'],
  tkDue: ['Due', 'תאריך יעד'],
  tkOn: ['On', 'על'],
  tkCreateBtn: ['Create task', 'יצירת משימה'],
  tkCreated: ['Task created', 'המשימה נוצרה'],
  tkStart: ['Start', 'התחלה'],
  tkMarkDone: ['Mark done', 'סימון כבוצע'],
  tkAskDecision: ['Needs a decision…', 'צריך החלטה…'],
  tkReopen: ['Reopen', 'פתיחה מחדש'],
  tkCancelTask: ['Cancel task', 'ביטול המשימה'],
  tkTake: ['Take it', 'אני לוקח/ת'],
  tkHandBack: ['Hand back', 'החזרה'],
  tkWaitingOn: ['Waiting on {name}', 'ממתין ל{name}'],
  tkNeedsNote: ['What do you need?', 'מה צריך?'],
  tkNeedsWho: ['Who must decide?', 'מי צריך להחליט?'],
  tkNeedsDefault: ['The task’s creator, or the project producer', 'מי שיצר/ה את המשימה, או המפיק/ה של הפרויקט'],
  tkSend: ['Send', 'שליחה'],
  tkHistory: ['History', 'היסטוריה'],
  tkCommentPh: ['Write a comment…', 'כתיבת הערה…'],
  tkOpenVideo: ['Open the video', 'לסרטון'],
  tkOpenEpisode: ['Open the episode', 'לפרק'],
  tkOpenProject: ['Open the project', 'לפרויקט'],
  tkFromPipeline: ['Opened by Castfeeds', 'נפתח על ידי Castfeeds'],
  tkFrom: ['Created by {name}', 'נוצר על ידי {name}'],
  tkDueOn: ['Due {when}', 'עד {when}'],
  tkNotFound: ['This task does not exist, or you can’t see it.', 'המשימה לא קיימת, או שאין לך גישה אליה.'],
  tkPick: ['Pick a task to see its brief and history.', 'בחרו משימה כדי לראות את הפרטים וההיסטוריה.'],
  tkEv_created: ['{actor} created the task', '{actor} יצר/ה את המשימה'],
  tkEv_created_trigger: ['Opened by Castfeeds', 'נפתחה על ידי Castfeeds'],
  tkEv_status: ['{actor} moved it to {status}', '{actor} העביר/ה ל{status}'],
  tkEv_assign: ['{actor} assigned it to {to}', '{actor} שייך/ה ל{to}'],
  tkEv_unassign: ['{actor} unassigned it', '{actor} ביטל/ה את השיוך'],
  tkEv_trigger: ['It happened again', 'זה קרה שוב'],
  tkEv_edit: ['{actor} edited the task', '{actor} ערך/ה את המשימה'],
  tkEv_resolved: ['Closed on its own: nothing left to do', 'נסגרה מעצמה: אין מה לעשות'],
  tkMyTasks: ['My tasks', 'המשימות שלי'],
  tkMyTasksEmpty: ['Nothing on your plate.', 'אין משימות פתוחות.'],
  tkSeeAll: ['See all', 'לכל המשימות'],
  tkOpenCount: ['{n} open', '{n} פתוחות'],
  trg_session_needs_hand: ['Check the recording of {episode}', 'לבדוק את ההקלטה של {episode}'],
  trg_changes_requested: ['Apply the changes to {video} (v{version})', 'לבצע את השינויים ב{video} (v{version})'],
  trg_customer_comment: ['Reply to {customer} on {video}', 'לענות ל{customer} על {video}'],
  trg_approval_deadline: ['Chase {customer}: {video} auto-approves soon', 'לפנות ל{customer}: {video} יאושר אוטומטית בקרוב'],
  trg_export_failed: ['Export failed: {episode}', 'הייצוא נכשל: {episode}'],
  trg_publication_held: ['Post held today: {video} on {platform}', 'פוסט מעוכב היום: {video} ב-{platform}'],
  trgName_session_needs_hand: ['A session needs a hand', 'הקלטה צריכה טיפול'],
  trgName_changes_requested: ['Changes requested on a video', 'התבקשו שינויים בסרטון'],
  trgName_customer_comment: ['A customer commented', 'לקוח הגיב'],
  trgName_approval_deadline: ['Close to the auto-approve deadline', 'קרוב למועד האישור האוטומטי'],
  trgName_export_failed: ['An export failed', 'ייצוא נכשל'],
  trgName_publication_held: ['A post is held on its date', 'פוסט מעוכב ביום הפרסום'],
  trgSub_session_needs_hand: ['A manual clap, a missing camera or mic, failed renders or a failed run.', 'מחיאת כף ידנית, מצלמה או מיקרופון חסרים, רינדורים שנכשלו או עיבוד שנכשל.'],
  trgSub_changes_requested: ['One task per version; more requests are added to it.', 'משימה אחת לכל גרסה; בקשות נוספות מתווספות אליה.'],
  trgSub_customer_comment: ['One reply task per video, collecting the customer’s comments.', 'משימת מענה אחת לכל סרטון, שאוספת את ההערות של הלקוח.'],
  trgSub_approval_deadline: ['A video waits with the customer and will be approved automatically soon.', 'סרטון ממתין ללקוח ויאושר אוטומטית בקרוב.'],
  trgSub_export_failed: ['A download pack could not be built.', 'לא הצלחנו לבנות חבילת הורדה.'],
  trgSub_publication_held: ['A post is due today but its video is still in approvals.', 'פוסט אמור לצאת היום אבל הסרטון עדיין בתהליך אישור.'],
  trgReason_manual_clap: ['Sync needs a manual clap', 'הסנכרון צריך מחיאת כף ידנית'],
  trgReason_no_camera: ['No camera was uploaded', 'לא הועלתה מצלמה'],
  trgReason_no_mic: ['No microphone was uploaded', 'לא הועלה מיקרופון'],
  trgReason_renders_failed: ['Some renders failed', 'חלק מהרינדורים נכשלו'],
  trgReason_session_failed: ['Processing failed', 'העיבוד נכשל'],
  setTabTasks: ['Tasks', 'משימות'],
  setTabNotifications: ['Notifications', 'התראות'],
  tkSetIntro: ['What opens a task by itself, and who it goes to. Each project sets its producer and editor on the project page.', 'מה פותח משימה מעצמו, ולמי היא הולכת. כל פרויקט מגדיר מפיק/ה ועורך/ת בעמוד הפרויקט.'],
  tkSetTz: ['Studio time zone', 'אזור הזמן של האולפן'],
  tkSetTzHint: ['For “a post is held on its date” and the default time of the daily summary.', 'עבור „פוסט מעוכב ביום הפרסום” ושעת ברירת המחדל של הסיכום היומי.'],
  tkSetGoesTo: ['Goes to', 'הולך אל'],
  tkAssign_producer: ['The project producer', 'המפיק/ה של הפרויקט'],
  tkAssign_editor: ['The project editor', 'העורך/ת של הפרויקט'],
  tkAssign_none: ['Nobody (unassigned)', 'אף אחד (לא משויך)'],
  tkSetHours: ['Hours before', 'שעות לפני'],
  tkSaved: ['Saved', 'נשמר'],
  ntIntro: ['E-mails about your tasks in this workspace.', 'מיילים על המשימות שלך בסביבת העבודה הזו.'],
  ntDigest: ['A daily summary of my open tasks', 'סיכום יומי של המשימות הפתוחות שלי'],
  ntDigestSub: ['Sent only on days you have open tasks.', 'נשלח רק בימים שיש לך משימות פתוחות.'],
  ntDigestHour: ['Send at', 'שעת שליחה'],
  ntTz: ['Time zone', 'אזור זמן'],
  ntTzStudio: ['The studio’s', 'של האולפן'],
  ntNeedsYou: ['E-mail me at once when a task needs me', 'לשלוח לי מייל מיד כשמשימה ממתינה לי'],
  ntNeedsYouSub: ['When someone moves a task to “Needs you” and names you.', 'כשמישהו מעביר משימה ל„ממתין לך” ובוחר בך.'],
  tkDefaults: ['Task defaults', 'ברירות מחדל למשימות'],
  tkDefaultsSub: ['Who gets the tasks Castfeeds opens on this project.', 'מי מקבל/ת את המשימות ש-Castfeeds פותח בפרויקט הזה.'],
  tkDefaultProducer: ['Producer', 'מפיק/ה'],
  tkDefaultEditor: ['Editor', 'עורך/ת'],
  tkNobody: ['Nobody', 'אף אחד'],
  actTitle: ['Activity', 'פעילות'],
  actEmpty: ['No activity yet.', 'עדיין אין פעילות.'],
  actMore: ['Show more', 'עוד'],
  actSystem: ['Castfeeds', 'Castfeeds'],
  act_approval_request: ['{actor} sent {video} to approval', '{actor} שלח/ה את {video} לאישור'],
  act_approval_approve: ['{actor} approved {video}', '{actor} אישר/ה את {video}'],
  act_approval_request_changes: ['{actor} asked for changes to {video}', '{actor} ביקש/ה שינויים ב{video}'],
  act_approval_auto_approve: ['{video} was approved automatically (no reply)', '{video} אושר אוטומטית (ללא תגובה)'],
  act_comment_create: ['{actor} commented on {video}', '{actor} הגיב/ה על {video}'],
  act_session_create: ['{actor} started a recording session for {episode}', '{actor} פתח/ה הקלטה ל{episode}'],
  act_session_process: ['{actor} started processing {episode}', '{actor} התחיל/ה את העיבוד של {episode}'],
  act_session_process_in_cloud: ['{actor} moved {episode} to the cloud', '{actor} העביר/ה את {episode} לענן'],
  act_transcript_correct: ['{actor} corrected the transcript of {episode}', '{actor} תיקן/ה את התמלול של {episode}'],
  act_edit_approve: ['{actor} approved {video} in the drill-down', '{actor} אישר/ה את {video} בעמוד הפירוק'],
  act_edit_reject: ['{actor} rejected {video}', '{actor} דחה/תה את {video}'],
  act_edit_cut: ['{actor} changed the cut of {video}', '{actor} שינה/תה את החיתוך של {video}'],
  act_edit_clip: ['{actor} made a clip: {video}', '{actor} יצר/ה קליפ: {video}'],
  act_edit_formats: ['{actor} made other formats of {video}', '{actor} יצר/ה פורמטים נוספים של {video}'],
  act_export_create: ['{actor} asked for a download pack of {episode}', '{actor} ביקש/ה חבילת הורדה של {episode}'],
  act_drilldown_regenerate: ['{actor} regenerated the clips of {episode}', '{actor} יצר/ה מחדש את הקליפים של {episode}'],
  act_publication_batch: ['{actor} scheduled {video}', '{actor} תזמן/ה את {video}'],
  act_publication_held: ['A post of {video} was held: the workspace was suspended', 'פוסט של {video} הושהה: סביבת העבודה הייתה מושעית'],
  act_project_create: ['{actor} created the project {project}', '{actor} יצר/ה את הפרויקט {project}'],
  act_task_created: ['{actor} created the task “{task}”', '{actor} יצר/ה את המשימה „{task}”'],
  act_task_created_system: ['A task opened: “{task}”', 'נפתחה משימה: „{task}”'],
  act_task_status: ['{actor} moved “{task}” to {status}', '{actor} העביר/ה את „{task}” ל{status}'],
  act_task_status_system: ['“{task}” moved to {status}', '„{task}” עברה ל{status}'],
  act_task_assign: ['{actor} assigned “{task}”', '{actor} שייך/ה את „{task}”'],
  act_task_comment: ['{actor} commented on “{task}”', '{actor} הגיב/ה על „{task}”'],
  prHere: ['{n} here now', '{n} כאן עכשיו'],
  prEditing: ['editing {video}', 'עורך/ת את {video}'],
  prViewing: ['viewing', 'צופה'],
  prYou: ['You', 'את/ה'],
  prConflict: ['{names} is also editing {video}. Your changes may overlap: agree who goes first.', '{names} עורך/ת גם את {video}. השינויים עלולים להתנגש: כדאי לתאם מי ממשיך/ה.'],
} as const satisfies Record<string, readonly [string, string]>;

export type StringKey = keyof typeof strings;
